import { useEffect, useState, useCallback, useRef } from "react";
import { getCurrencySymbol } from '../utils/currency';
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  doc,
  getDoc,
  setDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { readGlobalProfiles, readFlatTemplate } from "@services/firebaseGlobal";
import { getFirebase, getProdDb, getCurrentUserEnv } from "@services/firebase";
import {
  FaToggleOn,
  FaToggleOff,
  FaSync,
  FaTimes,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaChevronDown,
  FaChevronUp,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaRegCopy,
  FaStar,
} from "react-icons/fa";
import ImageZoomModal from "@components/modals/ImageZoomModal";
import { useGoogleDrive } from "../contexts/GoogleDriveContext";
import "./css/Order.css";

// Safely converts any Firestore value (Timestamp, object, etc.) to a renderable string.
const safeStr = (val, fallback = "N/A") => {
  if (val === null || val === undefined || val === "") return fallback;
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object") {
    // Firestore Timestamp (has seconds + nanoseconds)
    if (
      typeof val.seconds === "number" &&
      typeof val.nanoseconds === "number"
    ) {
      return new Date(val.seconds * 1000).toLocaleString();
    }
    // Native Date object
    if (val instanceof Date) return val.toLocaleString();
    // toDate() method (Firestore SDK Timestamp instance)
    if (typeof val.toDate === "function") return val.toDate().toLocaleString();
  }
  return fallback;
};

const Order = ({ userRole: propUserRole }) => {
  const { db, auth } = getFirebase();
  const [user] = useAuthState(auth);

  const [userRole, setUserRole] = useState(() => {
    // Use prop if available, otherwise try localStorage
    if (propUserRole) return propUserRole;
    try {
      const cachedRole = localStorage.getItem("userRole");
      return cachedRole || "";
    } catch (err) {
      console.error("Error reading userRole from localStorage:", err);
      return "";
    }
  });

  const [formsOrders, setFormsOrders] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [formsError, setFormsError] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "deliveryDate",
    direction: "asc",
  });
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [customerModal, setCustomerModal] = useState(null);
  const [tierModal, setTierModal] = useState(null);
  const [moreModal, setMoreModal] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [priceLoading, setPriceLoading] = useState(false);
  const [deliveryDateInput, setDeliveryDateInput] = useState("");
  const [deliveryDateLoading, setDeliveryDateLoading] = useState(false);
  const [deliveryTimeInput, setDeliveryTimeInput] = useState("");
  const [deliveryTimeLoading, setDeliveryTimeLoading] = useState(false);
  const [spongeTypeInput, setSpongeTypeInput] = useState("");
  const [spongeTypeLoading, setSpongeTypeLoading] = useState(false);
  const [editingSpongeTypeId, setEditingSpongeTypeId] = useState(null);
  const [tierCoverInput, setTierCoverInput] = useState("");
  const [tierCoverLoading, setTierCoverLoading] = useState(false);
  const [tierSpongeBaseInput, setTierSpongeBaseInput] = useState("");
  const [tierSpongeBaseLoading, setTierSpongeBaseLoading] = useState(false);
  const [tierCakeFlavourInput, setTierCakeFlavourInput] = useState("");
  const [tierCakeFlavourLoading, setTierCakeFlavourLoading] = useState(false);
  const [tierSpongeBase2Input, setTierSpongeBase2Input] = useState("");
  const [tierSpongeBase2Loading, setTierSpongeBase2Loading] = useState(false);
  const [tierCakeFlavour2Input, setTierCakeFlavour2Input] = useState("");
  const [tierCakeFlavour2Loading, setTierCakeFlavour2Loading] = useState(false);
  const [tierSpongeBase3Input, setTierSpongeBase3Input] = useState("");
  const [tierSpongeBase3Loading, setTierSpongeBase3Loading] = useState(false);
  const [tierCakeFlavour3Input, setTierCakeFlavour3Input] = useState("");
  const [tierCakeFlavour3Loading, setTierCakeFlavour3Loading] = useState(false);

  const [quantityInput, setQuantityInput] = useState("");
  const [quantityLoading, setQuantityLoading] = useState(false);
  const [editingQuantityId, setEditingQuantityId] = useState(null);
  const [orderHolidayCalendar, setOrderHolidayCalendar] = useState([]);
  const [orderTimeWindows, setOrderTimeWindows] = useState([]);
  const [showOldOrders, setShowOldOrders] = useState(false);
  const [appOrderDocs, setAppOrderDocs] = useState([]);
  const [appOldOrderDocs, setAppOldOrderDocs] = useState([]);
  const [userProfileMap, setUserProfileMap] = useState({});
  const getCurrentMonthKey = () => {
    return new Date().toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const {
    isDriveConnected,
    hasDriveAccess,
    connectToGoogleDrive,
    fetchFormResponses,
    fetchFormDetails,
    isLoading: driveLoading,
    error: driveError,
  } = useGoogleDrive();

  const convertGoogleDriveUrl = (fileId) => {
    return processImageUrl(fileId);
  };

  const validateImageUrl = (url) => {
    if (!url) return false;
    if (typeof url !== "string") return false;

    return (
      url.startsWith("http") &&
      (url.includes("drive.google.com") ||
        url.includes("cloudfunctions.net/driveImageProxy") ||
        url.includes("firebasestorage.googleapis.com"))
    );
  };

  const directFieldMapping = {
    name: "2d2f8286",
    phone: "018487ef",
    address: "7b9c0ab5",
    houseNumber: "3f2d97b1",
    pinCode: "6ac44f20",
    deliveryMode: "6b7a9871",
    deliveryDate: "1e9e8177",
    eventDate: "0cfefbbd",
    deliveryTime1: "1434b0aa",
    deliveryTime2: "05601c16",
    deliveryTime3: "403bfa9c",
    flavour: "6fd51743",
    spongeType: "28c98214",
    filling: "3265eef6",
    frosting: "6dadc1db",
    quantity: "323a51b2",
    otherQuantity: "4c5100c9",
    multiTier: "5e480906",
    moreTier: "7a88e37d",
    spongeFlavour2: "1d0fc68e",
    filling2: "6e5f41a9",
    spongeFlavour3: "64804669",
    filling3: "6ed177c5",
    message: "2f88abb1",
    cakeName: "6ddd3826",
    position: "449d2daa",
    specialRequest: "06cc5d19",
    designUpload: "49cfb9c1",
    streetName: "7b9c0ab5",
    weekendHoliday: "6f494489",
    agreement: "70c174ab",
  };

  // Initialize price input when customer modal opens
  useEffect(() => {
    if (customerModal && (userRole === "Admin" || userRole === "Super")) {
      // For app orders show ccost + del_cost as the total editable price
      const rawCost = customerModal._isAppOrder
        ? (customerModal.cost || "").replace(" €", "").replace("€", "").trim()
        : (customerModal.cost || "").replace(" €", "").replace("€", "").trim();
      const numericValue = rawCost;
      setPriceInput(numericValue);
      // Initialize delivery date (stored as YYYY-MM-DD)
      setDeliveryDateInput(customerModal.deliveryDate || "");
      setDeliveryTimeInput(customerModal.deliveryTime || "");
    } else {
      // Reset inputs when modal closes
      setPriceInput("");
      setDeliveryDateInput("");
      setDeliveryTimeInput("");
    }
  }, [customerModal, userRole]);

  // Initialize sponge type input when editing starts
  useEffect(() => {
    if (editingSpongeTypeId) {
      const order = formsOrders.find((o) => o.id === editingSpongeTypeId);
      if (order) {
        setSpongeTypeInput(order.spongeType || "");
      }
    } else {
      setSpongeTypeInput("");
    }
  }, [editingSpongeTypeId, formsOrders]);

  // Initialize quantity input when editing starts
  useEffect(() => {
    if (editingQuantityId) {
      const order = formsOrders.find((o) => o.id === editingQuantityId);
      if (order) {
        setQuantityInput(order.quantity || "");
      }
    } else {
      setQuantityInput("");
    }
  }, [editingQuantityId, formsOrders]);

  // Initialize tier modal inputs when tier modal opens
  useEffect(() => {
    if (tierModal && (userRole === "Admin" || userRole === "Super")) {
      setTierCoverInput(tierModal.frosting || "");
      setTierSpongeBaseInput(tierModal.flavour || "");
      setTierCakeFlavourInput(tierModal.filling || "");
      setTierSpongeBase2Input(tierModal.spongeFlavour2 || "");
      setTierCakeFlavour2Input(tierModal.filling2 || "");
      setTierSpongeBase3Input(tierModal.spongeFlavour3 || "");
      setTierCakeFlavour3Input(tierModal.filling3 || "");
    } else {
      // Reset inputs when modal closes
      setTierCoverInput("");
      setTierSpongeBaseInput("");
      setTierCakeFlavourInput("");
      setTierSpongeBase2Input("");
      setTierCakeFlavour2Input("");
      setTierSpongeBase3Input("");
      setTierCakeFlavour3Input("");
    }
  }, [tierModal, userRole]);

  // Load holiday calendar and time windows for the order modal
  useEffect(() => {
    if (!user || !db) return;
    Promise.all([
      readFlatTemplate("dHolidayCalendar"),
      readFlatTemplate("dTimewindow"),
    ])
      .then(([hc, tw]) => {
        setOrderHolidayCalendar(hc);
        setOrderTimeWindows(tw);
      })
      .catch((err) => console.error("Order: error loading calendar meta", err));
  }, [user, db]);

  useEffect(() => {
    if (!user || !db) return;

    const fetchUserRole = async () => {
      try {
        const userList = await readGlobalProfiles("cUsers");
        const currentUserData = userList.find(
          (userData) => userData.id === user.uid
        );
        if (currentUserData) {
          let role = "User";
          if (currentUserData.isadminuser) {
            role = "Admin";
          } else if (currentUserData.issuperuser) {
            role = "Super";
          } else if (currentUserData.ismover) {
            role = "Mover";
          }
          setUserRole(role);
          // Sync to localStorage
          try {
            localStorage.setItem("userRole", role);
          } catch (err) {
            console.error("Error saving userRole to localStorage:", err);
          }

          if (role === "Admin" || role === "Super") {
            setExpandedMonths((prev) => {
              const newSet = new Set(prev);
              newSet.add(getCurrentMonthKey());
              return newSet;
            });
          }
        } else {
          setUserRole("");
        }
      } catch (err) {
        console.error("Error fetching user role:", err);
        setUserRole("");
      }
    };

    // Only fetch if we don't have a role from prop
    if (!propUserRole) {
      fetchUserRole();
    } else {
      // Role is provided via props
      setUserRole(propUserRole);
      if (propUserRole === "Admin" || propUserRole === "Super") {
        setExpandedMonths((prev) => {
          const newSet = new Set(prev);
          newSet.add(getCurrentMonthKey());
          return newSet;
        });
      }
    }
  }, [user, propUserRole]);

  const processImageUrl = (imageUrlOrFileId) => {
    if (!imageUrlOrFileId) return null;

    let processedUrl = imageUrlOrFileId;

    // If it's a Google Drive URL or file ID, convert it to proxy URL
    if (
      imageUrlOrFileId.includes("drive.google.com") ||
      (!imageUrlOrFileId.includes("http") &&
        !imageUrlOrFileId.includes("cloudfunctions.net"))
    ) {
      // Extract file ID from Google Drive URL if needed
      let fileId = imageUrlOrFileId;
      if (imageUrlOrFileId.includes("drive.google.com")) {
        const match = imageUrlOrFileId.match(/[-\w]{25,}/);
        fileId = match ? match[0] : imageUrlOrFileId;
      }

      // Convert to proxy URL
      const proxyBaseUrl =
        import.meta.env.VITE_DRIVE_PROXY_URL ||
        "https://us-central1-pcakedatastore.cloudfunctions.net/driveImageProxy";
      processedUrl = `${proxyBaseUrl}?id=${fileId}&sz=w1000`;
    }

    // Validate the URL
    if (validateImageUrl(processedUrl)) {
      return processedUrl;
    }

    return null;
  };

  const normalizeAppOrder = (docData, sourceCollection) => {
    const uid = docData.userId || "";
    const userInfo = userProfileMap[uid] || {};
    const firstTier = (docData.tiers && docData.tiers[0]) || {};
    // sponge  = sponge base (Vanilla/Chocolate) → displayed as "Sponge"
    // cflavour = cake flavour (Salted Caramel)   → displayed as "Flavour"
    const spongeBase = docData.sponge || firstTier.sponge || "";
    const cakeFlavour = docData.cflavour || firstTier.cflavour || "";
    // For app orders, check eggless field (false = With Egg, true = Eggless)
    const eggless = docData.eggless;
    const spongeType =
      eggless === true ? "Eggless" : eggless === false ? "With Egg" : "";
    const tierCount = docData.tiers ? docData.tiers.length : 1;
    const cakeCost =
      docData.ccost != null ? parseFloat(String(docData.ccost)) : null;
    const delCost =
      docData.del_cost != null ? parseFloat(String(docData.del_cost)) : null;
    let cost = "";
    if (cakeCost != null) {
      const total = delCost != null ? cakeCost + delCost : cakeCost;
      cost = `${total} €`;
    } else if (docData.Cost) {
      cost = docData.Cost;
    }
    const cakeCostOnly = cakeCost != null ? String(cakeCost) : "";

    // Handle deliveryDate as Firestore Timestamp, JS Date, or plain string
    const rawDate = docData.deliveryDate;
    let deliveryDateStr = "";
    if (rawDate) {
      if (typeof rawDate === "object" && typeof rawDate.toDate === "function") {
        // Use local date to avoid UTC off-by-one
        const d = rawDate.toDate();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        deliveryDateStr = `${y}-${m}-${day}`;
      } else if (rawDate instanceof Date) {
        const y = rawDate.getFullYear();
        const m = String(rawDate.getMonth() + 1).padStart(2, "0");
        const day = String(rawDate.getDate()).padStart(2, "0");
        deliveryDateStr = `${y}-${m}-${day}`;
      } else {
        deliveryDateStr = convertDateFormat(String(rawDate)) || String(rawDate);
      }
    }
    // Fall back to creation date if deliveryDate is missing
    if (!deliveryDateStr) {
      const rawCreated = docData.created;
      if (rawCreated) {
        try {
          const cd =
            typeof rawCreated.toDate === "function"
              ? rawCreated.toDate()
              : rawCreated instanceof Date
              ? rawCreated
              : null;
          if (cd) {
            const y = cd.getFullYear();
            const m = String(cd.getMonth() + 1).padStart(2, "0");
            const day = String(cd.getDate()).padStart(2, "0");
            deliveryDateStr = `${y}-${m}-${day}`;
          }
        } catch (_) {
          /* ignore */
        }
      }
    }

    return {
      id: docData._id,
      _isAppOrder: true,
      _sourceCollection: sourceCollection,
      formsSource: false,
      userId: uid,
      email: userInfo.email || "",
      name: userInfo.name || "N/A",
      phone: userInfo.phone || "",
      address: docData.del_address || "",
      deliveryDate: deliveryDateStr,
      originalDeliveryDate: deliveryDateStr,
      eventDate: null,
      del_mode: docData.del_mode !== false,
      deliveryTime: docData.deliveryTime || "",
      quantity:
        typeof docData.quantity === "number"
          ? String(docData.quantity)
          : docData.quantity || "",
      flavour: spongeBase,
      filling: cakeFlavour,
      frosting: docData.frosting || docData.cover || "",
      spongeType: spongeType,
      tierCount,
      images: Array.isArray(docData.images)
        ? docData.images.map((u) => processImageUrl(u)).filter(Boolean)
        : [],
      theme: docData.theme || "N/A",
      tiers: (docData.tiers || []).map((t) => ({
        cflavour: t.cflavour || "",
        cfilling: t.cfilling || "",
        sponge: t.sponge || "",
      })),
      cover: docData.cover || docData.frosting || "",
      message: docData.disp_msg || docData.special_wish || "",
      specialRequest: "",
      cakeName: docData.name_cake || "",
      position: docData.msg_pos || "",
      spongeFlavour2: docData.tiers?.[1]?.sponge || "",
      filling2: docData.tiers?.[1]?.cflavour || "",
      spongeFlavour3: docData.tiers?.[2]?.sponge || "",
      filling3: docData.tiers?.[2]?.cflavour || "",
      createTime: "",
      lastSubmittedTime: "",
      cost,
      cakeCostOnly,
      _delCost: delCost ?? 0,
      reqState: docData.reqState || "",
      shopfeedback: docData.shopfeedback || null,
    };
  };

  const normalizePhoneNumber = (phone) => {
    // Remove all non-digit characters
    let normalized = phone.replace(/[^\d]/g, "");

    // Handle German phone numbers specifically
    if (normalized.startsWith("49")) {
      // Remove Germany country code
      normalized = normalized.substring(2);
    }

    // If it starts with 0, remove it (optional)
    if (normalized.startsWith("0")) {
      normalized = normalized.substring(1);
    }

    // Take only the first 10-15 digits (adjust as needed)
    return normalized.substring(0, 15);
  };

  const convertDateFormat = (dateString) => {
    if (!dateString || dateString === "N/A") return null;
    const s = dateString.trim();

    // DD.MM.YYYY or D.M.YYYY
    const dotMatch = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dotMatch) {
      const [, d, m, y] = dotMatch;
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // DD/MM/YYYY or D/M/YYYY (European slash)
    const slashEU = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashEU) {
      const [, d, m, y] = slashEU;
      // Treat as DD/MM/YYYY (day first) since this app is European-based
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }

    // YYYY/MM/DD
    const slashISO = s.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (slashISO) {
      const [, y, m, d] = slashISO;
      return `${y}-${m}-${d}`;
    }

    // Try native Date.parse as last resort (handles "January 25, 2025", etc.)
    const parsed = Date.parse(s);
    if (!isNaN(parsed)) {
      const dt = new Date(parsed);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const d = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    return null;
  };

  const storeAllFormsResponses = async (responses, formDetails) => {
    if (!user || !db || !(userRole === "Admin" || userRole === "Super")) {
      return;
    }

    try {
      const { doc, setDoc, getDoc } = await import("firebase/firestore");

      let processedCount = 0;
      let updatedCount = 0;

      for (const response of responses) {
        try {
          const answers = response.answers || {};

          const getAnswerValue = (fieldName) => {
            const questionId = directFieldMapping[fieldName];
            if (!questionId) return "";

            const answer = answers[questionId];
            if (!answer) return "";

            if (answer.textAnswers) {
              return answer.textAnswers.answers?.[0]?.value || "";
            }
            return "";
          };

          const deliveryDateStr = getAnswerValue("deliveryDate");
          if (!deliveryDateStr) continue;

          // Skip past orders — only sync current and future
          const parsedDelivery = convertDateFormat(deliveryDateStr);
          if (parsedDelivery) {
            const deliveryDate = new Date(parsedDelivery + "T00:00:00");
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (!isNaN(deliveryDate.getTime()) && deliveryDate < today)
              continue;
          }

          const respondentEmail = response.respondentEmail;
          if (!respondentEmail) continue;

          const docId = `${respondentEmail}_${deliveryDateStr}`.replace(
            /[^a-zA-Z0-9_\-@.]/g,
            "_"
          );

          const docRef = doc(db, "FormOrders", docId);
          const docSnap = await getDoc(docRef);

          // Get the latest submission time from the response
          const responseTimestamp =
            response.lastSubmittedTime || response.createTime;
          if (!responseTimestamp) continue;

          // Convert to Date object for comparison
          const responseTime = new Date(responseTimestamp);

          // Check if document exists
          if (docSnap.exists()) {
            const existingData = docSnap.data();

            // Get existing timestamp from stored data
            const existingTimestampStr = existingData["Timestamp"];
            let existingTime = new Date(0); // Default to epoch if no timestamp

            if (existingTimestampStr) {
              try {
                // Parse our custom timestamp format: "DD.MM.YYYY HH:MM:SS"
                const [datePart, timePart] = existingTimestampStr.split(" ");
                const [day, month, year] = datePart.split(".");
                const [hours, minutes, seconds] = timePart.split(":");

                existingTime = new Date(
                  parseInt(year),
                  parseInt(month) - 1, // Months are 0-indexed
                  parseInt(day),
                  parseInt(hours),
                  parseInt(minutes),
                  parseInt(seconds)
                );
              } catch (error) {
                console.error("Error parsing existing timestamp:", error);
              }
            }

            const responseTimeSeconds = Math.floor(
              responseTime.getTime() / 1000
            );
            const existingTimeSeconds = Math.floor(
              existingTime.getTime() / 1000
            );

            // If response is newer than existing data, update it
            if (responseTimeSeconds > existingTimeSeconds) {
              // Get all form data for update
              const name = getAnswerValue("name");
              const phone = getAnswerValue("phone");
              const normalizedPhone = normalizePhoneNumber(phone);
              const eventDate = getAnswerValue("eventDate");
              const deliveryMode = getAnswerValue("deliveryMode");
              const address = getAnswerValue("address");
              const houseNumber = getAnswerValue("houseNumber");
              const pinCode = getAnswerValue("pinCode");
              const streetName = getAnswerValue("streetName") || address;

              const deliveryTime1 = getAnswerValue("deliveryTime1");
              const deliveryTime2 = getAnswerValue("deliveryTime2");
              const deliveryTime3 = getAnswerValue("deliveryTime3");
              const deliveryTime =
                deliveryTime1 || deliveryTime2 || deliveryTime3 || "";

              const quantity = getAnswerValue("quantity");
              const otherQuantity = getAnswerValue("otherQuantity");
              const flavour = getAnswerValue("flavour");
              const spongeType = getAnswerValue("spongeType");
              const filling = getAnswerValue("filling");
              const frosting = getAnswerValue("frosting");
              const multiTier = getAnswerValue("multiTier");
              const moreTier = getAnswerValue("moreTier");
              const spongeFlavour2 = getAnswerValue("spongeFlavour2");
              const filling2 = getAnswerValue("filling2");
              const spongeFlavour3 = getAnswerValue("spongeFlavour3");
              const filling3 = getAnswerValue("filling3");
              const message = getAnswerValue("message");
              const specialRequest = getAnswerValue("specialRequest");
              const cakeName = getAnswerValue("cakeName");
              const position = getAnswerValue("position");
              const weekendHoliday = getAnswerValue("weekendHoliday");
              const agreement = getAnswerValue("agreement");

              const getFileUploadsString = () => {
                const questionId = directFieldMapping["designUpload"];
                if (!questionId || !answers[questionId]) return "";

                const answer = answers[questionId];
                if (!answer.fileUploadAnswers) return "";

                const fileIds =
                  answer.fileUploadAnswers.answers?.map((a) => a.fileId) || [];
                const proxyUrls = fileIds
                  .map((fileId) => {
                    const url = processImageUrl(fileId);
                    return url || "";
                  })
                  .filter((url) => url !== "");

                return proxyUrls.join(",");
              };

              const fileUploads = getFileUploadsString();

              // Process quantity properly
              let processedQuantity = "";
              if (
                quantity &&
                (quantity.includes("More/Other") || quantity.includes("Other"))
              ) {
                if (otherQuantity) {
                  const numberMatch = otherQuantity.match(/(\d+\.?\d*)/);
                  if (numberMatch) {
                    processedQuantity = `${numberMatch[1]} Kilogram`;
                  } else {
                    processedQuantity = `${otherQuantity} Kilogram`;
                  }
                } else {
                  processedQuantity = "Other Quantity";
                }
              } else if (quantity) {
                processedQuantity = quantity.replace(/\s*\([^)]*\)$/, "");
              }

              // Build address properly
              const addressParts = [];
              if (pinCode) addressParts.push(pinCode);
              if (houseNumber) addressParts.push(houseNumber);
              if (streetName) addressParts.push(streetName);
              else if (address) addressParts.push(address);

              const formattedAddress = addressParts.join(" ");

              // Format Timestamp
              const formatTimestamp = (timestamp) => {
                if (!timestamp)
                  return new Date().toLocaleString("en-GB").replace(",", "");
                try {
                  const date = new Date(timestamp);
                  const day = date.getDate().toString().padStart(2, "0");
                  const month = (date.getMonth() + 1)
                    .toString()
                    .padStart(2, "0");
                  const year = date.getFullYear();
                  const hours = date.getHours().toString().padStart(2, "0");
                  const minutes = date.getMinutes().toString().padStart(2, "0");
                  const seconds = date.getSeconds().toString().padStart(2, "0");
                  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
                } catch {
                  return new Date().toLocaleString("en-GB").replace(",", "");
                }
              };

              const updateData = {
                Address: formattedAddress,
                "Contact Person Full Name": name || "",
                "Delivery Time": deliveryTime,
                "Delivery on Weekends/Holidays?": weekendHoliday || "",
                "Delivery or Pick-Up": deliveryMode || "",
                "Delivery/Pick-Up Date": deliveryDateStr || "",
                "E-Mail-Address": respondentEmail || "",
                "Event Date": eventDate || "",
                "House Number": houseNumber || "",
                "I agreed to": agreement || "",
                "Inside Flavour/Filling": filling || "",
                "Inside Flavour/Filling_2": filling2 || "",
                "Inside Flavour/Filling_3": filling3 || "",
                "Local Phone Number": phone || "",
                NormalizedPhone: normalizedPhone,
                "Message (Best Wishes /# of Anniversary etc": message || "",
                "More Tier?": moreTier || "",
                "Multi Tier?": multiTier || "",
                "Name on the Cake": cakeName || "",
                "Name/Email": name || "",
                "Other Quantity (Kilogram)": otherQuantity || "",
                "Outside Cover/Frosting": frosting || "",
                "Pin Code": pinCode || "",
                "Position on the Cake": position || "",
                Quantity: quantity || "",
                "Quantity (Kilo / # Person)": processedQuantity,
                "Special Request": specialRequest || "",
                "Sponge Flavour": flavour || "",
                "Sponge Flavour_2": spongeFlavour2 || "",
                "Sponge Flavour_3": spongeFlavour3 || "",
                "Sponge Type": spongeType || "",
                "Street Name": streetName || "",
                "Time Range": deliveryTime1 || "",
                "Time Range_2": deliveryTime2 || "",
                "Time Range_3": deliveryTime3 || "",
                Timestamp: formatTimestamp(responseTimestamp),
                "Upload Wished Design (Max 10 MB or 5 Files)":
                  fileUploads || "",
                updated: new Date().toISOString(),
              };

              // Preserve existing Cost if it exists
              if (existingData.Cost && existingData.Cost.trim() !== "") {
                updateData["Cost"] = existingData.Cost;
              } else {
                updateData["Cost"] = "";
              }

              // Merge with existing data to keep other fields that might exist
              const mergedData = {
                ...existingData,
                ...updateData,
                _autoGenerated: true, // Keep this flag
                _createdAt:
                  existingData["_createdAt"] || new Date().toISOString(), // Keep original creation date
              };

              await setDoc(docRef, mergedData);
              updatedCount++;
            } else {
              // Response is older or same, skip
              processedCount++;
            }
            continue;
          }

          // Document doesn't exist, create new one
          // Get all form data for new document
          const name = getAnswerValue("name");
          const phone = getAnswerValue("phone");
          const normalizedPhone = normalizePhoneNumber(phone);
          const eventDate = getAnswerValue("eventDate");
          const deliveryMode = getAnswerValue("deliveryMode");
          const address = getAnswerValue("address");
          const houseNumber = getAnswerValue("houseNumber");
          const pinCode = getAnswerValue("pinCode");
          const streetName = getAnswerValue("streetName") || address;

          const deliveryTime1 = getAnswerValue("deliveryTime1");
          const deliveryTime2 = getAnswerValue("deliveryTime2");
          const deliveryTime3 = getAnswerValue("deliveryTime3");
          const deliveryTime =
            deliveryTime1 || deliveryTime2 || deliveryTime3 || "";

          const quantity = getAnswerValue("quantity");
          const otherQuantity = getAnswerValue("otherQuantity");
          const flavour = getAnswerValue("flavour");
          const spongeType = getAnswerValue("spongeType");
          const filling = getAnswerValue("filling");
          const frosting = getAnswerValue("frosting");
          const multiTier = getAnswerValue("multiTier");
          const moreTier = getAnswerValue("moreTier");
          const spongeFlavour2 = getAnswerValue("spongeFlavour2");
          const filling2 = getAnswerValue("filling2");
          const spongeFlavour3 = getAnswerValue("spongeFlavour3");
          const filling3 = getAnswerValue("filling3");
          const message = getAnswerValue("message");
          const specialRequest = getAnswerValue("specialRequest");
          const cakeName = getAnswerValue("cakeName");
          const position = getAnswerValue("position");
          const weekendHoliday = getAnswerValue("weekendHoliday");
          const agreement = getAnswerValue("agreement");

          const getFileUploadsString = () => {
            const questionId = directFieldMapping["designUpload"];
            if (!questionId || !answers[questionId]) return "";

            const answer = answers[questionId];
            if (!answer.fileUploadAnswers) return "";

            const fileIds =
              answer.fileUploadAnswers.answers?.map((a) => a.fileId) || [];
            const proxyUrls = fileIds
              .map((fileId) => {
                const url = convertGoogleDriveUrl(fileId);
                return url || "";
              })
              .filter((url) => url !== "");

            return proxyUrls.join(",");
          };

          const fileUploads = getFileUploadsString();

          // Process quantity properly
          let processedQuantity = "";
          if (
            quantity &&
            (quantity.includes("More/Other") || quantity.includes("Other"))
          ) {
            if (otherQuantity) {
              const numberMatch = otherQuantity.match(/(\d+\.?\d*)/);
              if (numberMatch) {
                processedQuantity = `${numberMatch[1]} Kilogram`;
              } else {
                processedQuantity = `${otherQuantity} Kilogram`;
              }
            } else {
              processedQuantity = "Other Quantity";
            }
          } else if (quantity) {
            processedQuantity = quantity.replace(/\s*\([^)]*\)$/, "");
          }

          // Build address properly
          const addressParts = [];
          if (pinCode) addressParts.push(pinCode);
          if (houseNumber) addressParts.push(houseNumber);
          if (streetName) addressParts.push(streetName);
          else if (address) addressParts.push(address);

          const formattedAddress = addressParts.join(" ");

          // Format Timestamp
          const formatTimestamp = (timestamp) => {
            if (!timestamp)
              return new Date().toLocaleString("en-GB").replace(",", "");
            try {
              const date = new Date(timestamp);
              const day = date.getDate().toString().padStart(2, "0");
              const month = (date.getMonth() + 1).toString().padStart(2, "0");
              const year = date.getFullYear();
              const hours = date.getHours().toString().padStart(2, "0");
              const minutes = date.getMinutes().toString().padStart(2, "0");
              const seconds = date.getSeconds().toString().padStart(2, "0");
              return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
            } catch {
              return new Date().toLocaleString("en-GB").replace(",", "");
            }
          };

          const documentData = {
            Address: formattedAddress,
            "Contact Person Full Name": name || "",
            "Delivery Time": deliveryTime,
            "Delivery on Weekends/Holidays?": weekendHoliday || "",
            "Delivery or Pick-Up": deliveryMode || "",
            "Delivery/Pick-Up Date": deliveryDateStr || "",
            "E-Mail-Address": respondentEmail || "",
            "Event Date": eventDate || "",
            "House Number": houseNumber || "",
            "I agreed to": agreement || "",
            "Inside Flavour/Filling": filling || "",
            "Inside Flavour/Filling_2": filling2 || "",
            "Inside Flavour/Filling_3": filling3 || "",
            "Local Phone Number": phone || "",
            NormalizedPhone: normalizedPhone,
            "Message (Best Wishes /# of Anniversary etc": message || "",
            "More Tier?": moreTier || "",
            "Multi Tier?": multiTier || "",
            "Name on the Cake": cakeName || "",
            "Name/Email": name || "",
            "Other Quantity (Kilogram)": otherQuantity || "",
            "Outside Cover/Frosting": frosting || "",
            "Pin Code": pinCode || "",
            "Position on the Cake": position || "",
            Quantity: quantity || "",
            "Quantity (Kilo / # Person)": processedQuantity,
            "Special Request": specialRequest || "",
            "Sponge Flavour": flavour || "",
            "Sponge Flavour_2": spongeFlavour2 || "",
            "Sponge Flavour_3": spongeFlavour3 || "",
            "Sponge Type": spongeType || "",
            "Street Name": streetName || "",
            "Time Range": deliveryTime1 || "",
            "Time Range_2": deliveryTime2 || "",
            "Time Range_3": deliveryTime3 || "",
            Timestamp: formatTimestamp(responseTimestamp),
            "Upload Wished Design (Max 10 MB or 5 Files)": fileUploads || "",
            Cost: "",
            _autoGenerated: true,
            _createdAt: new Date().toISOString(),
            updated: new Date().toISOString(),
          };

          await setDoc(docRef, documentData);
          processedCount++;
        } catch (error) {
          console.error("Error processing response:", error);
        }
      }
    } catch (error) {
      console.error("Error in storeAllFormsResponses:", error);
    }
  };

  const archiveOldOrders = async () => {
    if (!user || !db) return;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const snapshot = await getDocs(collection(db, "FormOrders"));

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const dateStr = data["Delivery/Pick-Up Date"];
        if (!dateStr) continue;

        const converted = convertDateFormat(dateStr);
        if (!converted) continue;

        const deliveryDate = new Date(converted + "T00:00:00");
        if (isNaN(deliveryDate.getTime())) continue;

        if (deliveryDate < today) {
          try {
            const archiveRef = doc(db, "FormOrderOld", docSnap.id);
            const formOrderRef = doc(db, "FormOrders", docSnap.id);
            // Only write to FormOrderOld if not already archived there
            const existing = await getDoc(archiveRef);
            if (!existing.exists()) {
              await setDoc(archiveRef, data);
            }
            await deleteDoc(formOrderRef);
          } catch (docError) {
            console.error(
              `Failed to archive order ${docSnap.id}:`,
              docError.message
            );
          }
        }
      }
    } catch (error) {
      console.error("Error archiving old orders:", error);
    }
  };

  const syncFormsToFirestore = async () => {
    if (!user || !(userRole === "Admin" || userRole === "Super")) {
      return false;
    }

    // Drive / FormOrders sync is disabled in the Test environment
    const isTestEnv = getCurrentUserEnv() === 'Test';

    try {
      if (!isTestEnv && !isDriveConnected && hasDriveAccess()) {
        const result = await connectToGoogleDrive();
        if (result?.redirecting) return false;
      }

      if (!isTestEnv && isDriveConnected && hasDriveAccess()) {
        const formId = import.meta.env.VITE_INITIAL_RESPONSE_FORM_ID;
        if (formId) {
          const [formDetails, responses] = await Promise.all([
            fetchFormDetails(formId),
            fetchFormResponses(formId),
          ]);

          if (responses && responses.length > 0) {
            await storeAllFormsResponses(responses, formDetails);
          }
        }
      }

      // Always run archiving regardless of drive connection
      await archiveOldOrders();
      return true;
    } catch (error) {
      console.error("Sync to Firestore error:", error);
    }
    return false;
  };

  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (!user || !(userRole === "Admin" || userRole === "Super")) return;
    if (hasSyncedRef.current) return;
    // Don't lock if Drive access isn't ready yet — allow retry when role loads
    if (!hasDriveAccess()) return;
    hasSyncedRef.current = true;

    const autoSyncOnLoad = async () => {
      setTimeout(async () => {
        await syncFormsToFirestore();
        fetchFormsData();
      }, 1000);
    };

    autoSyncOnLoad();
  }, [user, userRole, isDriveConnected, hasDriveAccess]);

  // Load user profile map for resolving order/oldOrder owner details
  useEffect(() => {
    if (!user || !db || (userRole !== "Admin" && userRole !== "Super")) return;
    const loadUserProfileMap = async () => {
      try {
        const [profileSnap, userList] = await Promise.all([
          getDocs(collection(getProdDb(), "profile")),
          readGlobalProfiles("cUsers"),
        ]);
        const map = {};
        userList.forEach((u) => {
          map[u.id] = { email: u.email || "", env: u.env || 'Prod' };
        });
        profileSnap.docs.forEach((d) => {
          const pd = d.data();
          map[d.id] = {
            ...(map[d.id] || {}),
            name: [pd.f_name, pd.l_name].filter(Boolean).join(" ") || "",
            email: pd.email || map[d.id]?.email || "",
            phone: pd.phone || "",
            env: map[d.id]?.env || 'Prod',
          };
        });
        setUserProfileMap(map);
      } catch (e) {
        console.error("Order: error loading user profile map:", e);
      }
    };
    loadUserProfileMap();
  }, [user, userRole]);

  // Real-time listener for 'order' collection (active in-app orders)
  useEffect(() => {
    if (!user || !db || (userRole !== "Admin" && userRole !== "Super")) return;
    const unsub = onSnapshot(
      query(collection(db, "order")),
      (snap) =>
        setAppOrderDocs(snap.docs.map((d) => ({ _id: d.id, ...d.data() }))),
      (err) => console.error("Order: order snapshot error:", err)
    );
    return () => unsub();
  }, [user, userRole]);

  // Real-time listener for 'oldOrder' collection (completed in-app orders)
  useEffect(() => {
    if (!user || !db || (userRole !== "Admin" && userRole !== "Super")) return;
    const unsub = onSnapshot(
      query(collection(db, "oldOrder")),
      (snap) =>
        setAppOldOrderDocs(snap.docs.map((d) => ({ _id: d.id, ...d.data() }))),
      (err) => console.error("Order: oldOrder snapshot error:", err)
    );
    return () => unsub();
  }, [user, userRole]);

  const fetchFormsData = useCallback(async () => {
    if (!user || !db) return;

    setLoadingForms(true);
    setFormsError(null);

    try {
      if (userRole !== "Admin" && userRole !== "Super") {
        setFormsError(
          "Access denied. Only Admin and Super users can access order data."
        );
        setLoadingForms(false);
        return;
      }

      // Always fetch FormOrders (current/upcoming orders)
      const newOrdersSnapshot = await getDocs(collection(db, "FormOrders"));
      const seenIds = new Set();
      const allDocEntries = [];

      newOrdersSnapshot.forEach((docSnap) => {
        seenIds.add(docSnap.id);
        allDocEntries.push({ doc: docSnap, sourceCollection: "FormOrders" });
      });

      // When showing all orders, also include FormOrderOld (historical orders)
      if (showOldOrders) {
        const oldOrdersSnapshot = await getDocs(collection(db, "FormOrderOld"));
        oldOrdersSnapshot.forEach((docSnap) => {
          if (!seenIds.has(docSnap.id)) {
            // FormOrders takes precedence for duplicates
            allDocEntries.push({
              doc: docSnap,
              sourceCollection: "FormOrderOld",
            });
          }
        });
      }

      const mappedOrders = [];

      allDocEntries.forEach(({ doc, sourceCollection }) => {
        const data = doc.data();

        // Get delivery date from the document
        const deliveryDateStr = data["Delivery/Pick-Up Date"];
        if (!deliveryDateStr) return;

        const deliveryDate = convertDateFormat(deliveryDateStr);
        if (!deliveryDate) return;

        const name = data["Contact Person Full Name"] || "";
        const email = data["E-Mail-Address"] || "";
        const phone = data["Local Phone Number"] || "";
        const address = data["Address"] || "";
        const deliveryTime = data["Delivery Time"] || "";
        const deliveryMode = data["Delivery or Pick-Up"] || "";
        const flavour = data["Sponge Flavour"] || "";
        const filling = data["Inside Flavour/Filling"] || "";
        const frosting = data["Outside Cover/Frosting"] || "";
        const spongeType = data["Sponge Type"] || "";
        const eventDate = data["Event Date"] || "";
        const quantity = data["Quantity (Kilo / # Person)"] || "";
        const otherQuantity = data["Other Quantity (Kilogram)"] || "";
        const multiTier = data["Multi Tier?"] || "";
        const moreTier = data["More Tier?"] || "";
        const spongeFlavour2 = data["Sponge Flavour_2"] || "";
        const filling2 = data["Inside Flavour/Filling_2"] || "";
        const spongeFlavour3 = data["Sponge Flavour_3"] || "";
        const filling3 = data["Inside Flavour/Filling_3"] || "";
        const message =
          data["Message (Best Wishes /# of Anniversary etc"] || "";
        const specialRequest = data["Special Request"] || "";
        const cakeName = data["Name on the Cake"] || "";
        const position = data["Position on the Cake"] || "";
        const cost = data["Cost"] || "";

        // Get image URLs
        const imageUrls =
          data["Upload Wished Design (Max 10 MB or 5 Files)"] || "";
        const rawImages = imageUrls
          ? imageUrls.split(",").filter((url) => url.trim() !== "")
          : [];
        const images = rawImages
          .map((url) => processImageUrl(url))
          .filter((url) => url !== null);

        // Calculate tier count
        let tierCount = 1;
        if (multiTier) {
          const multiTierLower = multiTier.toLowerCase();
          if (multiTierLower === "yes" || multiTierLower === "true") {
            tierCount = 2;
            if (moreTier) {
              const moreTierLower = moreTier.toLowerCase();
              if (moreTierLower === "yes" || moreTierLower === "true") {
                tierCount = 3;
              }
            }
          }
        }

        // Build tiers array
        const tiers = [];
        if (tierCount >= 1) {
          tiers.push({
            cflavour: filling || "", // cake flavour from "Inside Flavour/Filling"
            cfilling: spongeType || "", // sponge type (Eggless/With Egg) from "Sponge Type"
            sponge: flavour || "", // sponge base from "Sponge Flavour"
          });
        }
        if (tierCount >= 2) {
          tiers.push({
            cflavour: filling2 || "", // cake flavour from "Inside Flavour/Filling_2"
            cfilling: spongeType || "", // sponge type (Eggless/With Egg) from "Sponge Type"
            sponge: spongeFlavour2 || "", // sponge base from "Sponge Flavour_2"
          });
        }
        if (tierCount >= 3) {
          tiers.push({
            cflavour: filling3 || "", // cake flavour from "Inside Flavour/Filling_3"
            cfilling: spongeType || "", // sponge type (Eggless/With Egg) from "Sponge Type"
            sponge: spongeFlavour3 || "", // sponge base from "Sponge Flavour_3"
          });
        }

        let processedQuantity = "";
        if (
          quantity &&
          (quantity.includes("More/Other") || quantity.includes("Other"))
        ) {
          if (otherQuantity) {
            const numberMatch = otherQuantity.match(/(\d+\.?\d*)/);
            if (numberMatch) {
              processedQuantity = `${numberMatch[1]} Kilogram`;
            } else {
              processedQuantity = `${otherQuantity} Kilogram`;
            }
          } else {
            processedQuantity = "Other Quantity";
          }
        } else if (quantity) {
          processedQuantity = quantity.replace(/\s*\([^)]*\)$/, "");
        }

        mappedOrders.push({
          id: doc.id,
          formsSource: true,
          _sourceCollection: sourceCollection,
          email: email,
          name: name || "Unknown",
          phone: phone,
          deliveryDate: deliveryDate,
          originalDeliveryDate: deliveryDateStr,
          eventDate: convertDateFormat(eventDate) || null,
          del_mode: deliveryMode?.toLowerCase().includes("delivery") || false,
          address: address,
          deliveryTime: deliveryTime,
          quantity: processedQuantity,
          flavour: flavour,
          filling: filling,
          frosting: frosting,
          spongeType: spongeType,
          tierCount: tierCount,
          images: images,
          theme: "N/A",
          tiers: tiers,
          cover: frosting,
          message: message,
          specialRequest: specialRequest,
          cakeName: cakeName,
          position: position,
          spongeFlavour2: spongeFlavour2,
          filling2: filling2,
          spongeFlavour3: spongeFlavour3,
          filling3: filling3,
          createTime: data["Timestamp"] || "",
          lastSubmittedTime: data["Timestamp"] || "",
          cost: cost,
          shopfeedback: data.shopfeedback || null,
        });
      });

      // Filter out orders with no name
      let finalOrders = mappedOrders.filter(
        (order) => order.name !== "Unknown"
      );

      // When not showing old orders, only show today and future delivery dates
      if (!showOldOrders) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        finalOrders = finalOrders.filter((order) => {
          if (!order.deliveryDate) return true;
          const d = new Date(order.deliveryDate + "T00:00:00");
          return !isNaN(d.getTime()) && d >= today;
        });
      }

      setFormsOrders(finalOrders);
    } catch (error) {
      console.error("Error loading order data:", error);
      setFormsError(`Failed to load order data: ${error.message}`);
    } finally {
      setLoadingForms(false);
    }
  }, [user, userRole, showOldOrders]);

  const handleSyncForms = async () => {
    setLoadingForms(true);
    const success = await syncFormsToFirestore();
    if (success) {
      await fetchFormsData();
    } else {
      setLoadingForms(false);
    }
  };

  useEffect(() => {
    if (userRole === "Admin" || userRole === "Super") {
      fetchFormsData();
    } else {
      setFormsOrders([]);
    }
  }, [userRole, showOldOrders]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  const toggleMonthGroup = (monthKey) => {
    setExpandedMonths((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(monthKey)) {
        newSet.delete(monthKey);
      } else {
        newSet.add(monthKey);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      let date;

      if (dateString.includes("UTC")) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(dateString + "T00:00:00");
      } else if (dateString.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
        const [day, month, year] = dateString.split(".");
        date = new Date(
          `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`
        );
      } else {
        date = new Date(dateString + "T00:00:00");
      }

      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const handleOrderClick = (order) => {
    if (userRole !== "Admin" && userRole !== "Super") return;
    setCustomerModal(order);
    document.body.style.overflow = "hidden";
  };

  const closeCustomerModal = () => {
    setCustomerModal(null);
    document.body.style.overflow = "auto";
  };

  const handleTierClick = (order) => {
    setTierModal(order);
    document.body.style.overflow = "hidden";
  };

  const handleMoreClick = (order) => {
    setMoreModal(order);
    document.body.style.overflow = "hidden";
  };

  const closeTierModal = () => {
    setTierModal(null);
    setEditingSpongeTypeId(null);
    setEditingQuantityId(null);
    document.body.style.overflow = "auto";
  };

  const closeMoreModal = () => {
    setMoreModal(null);
    setEditingSpongeTypeId(null);
    setEditingQuantityId(null);
    document.body.style.overflow = "auto";
  };

  // Helper function to check if an order is new (not past delivery)
  const isNewOrder = (order) => {
    if (!order.deliveryDate) return false;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const deliveryDate = new Date(order.deliveryDate + "T00:00:00");
      return !isNaN(deliveryDate.getTime()) && deliveryDate >= today;
    } catch (error) {
      console.error("Error checking if order is new:", error);
      return false;
    }
  };

  const saveSpongeType = async (orderId, spongeTypeValue) => {
    if (
      !user ||
      !db ||
      !orderId ||
      spongeTypeLoading ||
      !spongeTypeValue.trim()
    )
      return;

    try {
      setSpongeTypeLoading(true);
      const order = formsOrders.find((o) => o.id === orderId);
      if (!order) return;

      const docRef = doc(db, order._sourceCollection || "FormOrders", orderId);
      const updatePayload = order._isAppOrder
        ? {
            eggless: spongeTypeValue.trim() === "Eggless",
            updated: new Date().toISOString(),
          }
        : {
            "Sponge Type": spongeTypeValue.trim(),
            updated: new Date().toISOString(),
          };

      await setDoc(docRef, updatePayload, { merge: true });

      // Update local state
      setFormsOrders(
        formsOrders.map((o) =>
          o.id === orderId ? { ...o, spongeType: spongeTypeValue.trim() } : o
        )
      );

      setEditingSpongeTypeId(null);
    } catch (error) {
      console.error("Error saving sponge type:", error);
    } finally {
      setSpongeTypeLoading(false);
    }
  };

  const saveQuantity = async (orderId, quantityValue) => {
    if (!user || !db || !orderId || quantityLoading || !quantityValue.trim())
      return;

    try {
      setQuantityLoading(true);
      const order = formsOrders.find((o) => o.id === orderId);
      if (!order) return;

      const docRef = doc(db, order._sourceCollection || "FormOrders", orderId);

      // For app orders, quantity is stored as a number
      // For form orders, quantity is stored as a string in 'Quantity (Kilo / # Person)'
      const updatePayload = order._isAppOrder
        ? {
            quantity: quantityValue.trim(),
            updated: new Date().toISOString(),
          }
        : {
            "Quantity (Kilo / # Person)": quantityValue.trim(),
            updated: new Date().toISOString(),
          };

      await setDoc(docRef, updatePayload, { merge: true });

      // Update local state
      setFormsOrders(
        formsOrders.map((o) =>
          o.id === orderId ? { ...o, quantity: quantityValue.trim() } : o
        )
      );

      setEditingQuantityId(null);
    } catch (error) {
      console.error("Error saving quantity:", error);
    } finally {
      setQuantityLoading(false);
    }
  };

  const saveTierCover = async () => {
    if (
      !user ||
      !db ||
      !tierModal?.id ||
      tierCoverLoading ||
      !tierCoverInput.trim()
    )
      return;
    if (tierCoverInput.trim() === (tierModal.frosting || "")) return;

    try {
      setTierCoverLoading(true);
      const docRef = doc(
        db,
        tierModal._sourceCollection || "FormOrders",
        tierModal.id
      );
      const updatePayload = tierModal._isAppOrder
        ? { frosting: tierCoverInput.trim(), updated: new Date().toISOString() }
        : {
            "Outside Cover/Frosting": tierCoverInput.trim(),
            updated: new Date().toISOString(),
          };

      await setDoc(docRef, updatePayload, { merge: true });

      // Update local state
      setFormsOrders(
        formsOrders.map((o) =>
          o.id === tierModal.id ? { ...o, frosting: tierCoverInput.trim() } : o
        )
      );

      // Update tier modal state
      setTierModal({ ...tierModal, frosting: tierCoverInput.trim() });
    } catch (error) {
      console.error("Error saving tier cover:", error);
    } finally {
      setTierCoverLoading(false);
    }
  };

  const saveTierSpongeBase = async (tierNumber = 1) => {
    if (!user || !db || !tierModal?.id) return;

    let inputValue,
      loadingState,
      setLoadingState,
      fieldName,
      appFieldName,
      stateFieldName;

    if (tierNumber === 1) {
      if (tierSpongeBaseLoading || !tierSpongeBaseInput.trim()) return;
      if (tierSpongeBaseInput.trim() === (tierModal.flavour || "")) return;
      inputValue = tierSpongeBaseInput.trim();
      loadingState = tierSpongeBaseLoading;
      setLoadingState = setTierSpongeBaseLoading;
      fieldName = "Sponge Flavour";
      appFieldName = "flavour";
      stateFieldName = "flavour";
    } else if (tierNumber === 2) {
      if (tierSpongeBase2Loading || !tierSpongeBase2Input.trim()) return;
      if (tierSpongeBase2Input.trim() === (tierModal.spongeFlavour2 || ""))
        return;
      inputValue = tierSpongeBase2Input.trim();
      loadingState = tierSpongeBase2Loading;
      setLoadingState = setTierSpongeBase2Loading;
      fieldName = "Sponge Flavour 2";
      appFieldName = "spongeFlavour2";
      stateFieldName = "spongeFlavour2";
    } else if (tierNumber === 3) {
      if (tierSpongeBase3Loading || !tierSpongeBase3Input.trim()) return;
      if (tierSpongeBase3Input.trim() === (tierModal.spongeFlavour3 || ""))
        return;
      inputValue = tierSpongeBase3Input.trim();
      loadingState = tierSpongeBase3Loading;
      setLoadingState = setTierSpongeBase3Loading;
      fieldName = "Sponge Flavour 3";
      appFieldName = "spongeFlavour3";
      stateFieldName = "spongeFlavour3";
    } else {
      return;
    }

    try {
      setLoadingState(true);
      const docRef = doc(
        db,
        tierModal._sourceCollection || "FormOrders",
        tierModal.id
      );
      const updatePayload = tierModal._isAppOrder
        ? { [appFieldName]: inputValue, updated: new Date().toISOString() }
        : { [fieldName]: inputValue, updated: new Date().toISOString() };

      await setDoc(docRef, updatePayload, { merge: true });

      // Update local state
      setFormsOrders(
        formsOrders.map((o) =>
          o.id === tierModal.id ? { ...o, [stateFieldName]: inputValue } : o
        )
      );

      // Update tier modal state
      setTierModal({ ...tierModal, [stateFieldName]: inputValue });
    } catch (error) {
      console.error(`Error saving tier ${tierNumber} sponge:`, error);
    } finally {
      setLoadingState(false);
    }
  };

  const saveTierCakeFlavour = async (tierNumber = 1) => {
    if (!user || !db || !tierModal?.id) return;

    let inputValue,
      loadingState,
      setLoadingState,
      fieldName,
      appFieldName,
      stateFieldName;

    if (tierNumber === 1) {
      if (tierCakeFlavourLoading || !tierCakeFlavourInput.trim()) return;
      if (tierCakeFlavourInput.trim() === (tierModal.filling || "")) return;
      inputValue = tierCakeFlavourInput.trim();
      loadingState = tierCakeFlavourLoading;
      setLoadingState = setTierCakeFlavourLoading;
      fieldName = "Inside Flavour/Filling";
      appFieldName = "filling";
      stateFieldName = "filling";
    } else if (tierNumber === 2) {
      if (tierCakeFlavour2Loading || !tierCakeFlavour2Input.trim()) return;
      if (tierCakeFlavour2Input.trim() === (tierModal.filling2 || "")) return;
      inputValue = tierCakeFlavour2Input.trim();
      loadingState = tierCakeFlavour2Loading;
      setLoadingState = setTierCakeFlavour2Loading;
      fieldName = "Inside Flavour/Filling 2";
      appFieldName = "filling2";
      stateFieldName = "filling2";
    } else if (tierNumber === 3) {
      if (tierCakeFlavour3Loading || !tierCakeFlavour3Input.trim()) return;
      if (tierCakeFlavour3Input.trim() === (tierModal.filling3 || "")) return;
      inputValue = tierCakeFlavour3Input.trim();
      loadingState = tierCakeFlavour3Loading;
      setLoadingState = setTierCakeFlavour3Loading;
      fieldName = "Inside Flavour/Filling 3";
      appFieldName = "filling3";
      stateFieldName = "filling3";
    } else {
      return;
    }

    try {
      setLoadingState(true);
      const docRef = doc(
        db,
        tierModal._sourceCollection || "FormOrders",
        tierModal.id
      );
      const updatePayload = tierModal._isAppOrder
        ? { [appFieldName]: inputValue, updated: new Date().toISOString() }
        : { [fieldName]: inputValue, updated: new Date().toISOString() };

      await setDoc(docRef, updatePayload, { merge: true });

      // Update local state
      setFormsOrders(
        formsOrders.map((o) =>
          o.id === tierModal.id ? { ...o, [stateFieldName]: inputValue } : o
        )
      );

      // Update tier modal state
      setTierModal({ ...tierModal, [stateFieldName]: inputValue });
    } catch (error) {
      console.error(`Error saving tier ${tierNumber} flavour:`, error);
    } finally {
      setLoadingState(false);
    }
  };

  const applyFormsFiltersAndGrouping = () => {
    const todayForFilter = new Date();
    todayForFilter.setHours(0, 0, 0, 0);

    // Normalize and merge in-app orders from 'order' collection.
    // Active orders are ALWAYS shown regardless of date – they are live, in-progress orders.
    // In "New Orders" mode, hide delivered states (stale/test records); "All Orders" shows everything.
    const appOrders = appOrderDocs
      .filter((d) => {
        if (d.s_delete || d.reqState === "Order Cancelled") return false;
        if (
          !showOldOrders &&
          (d.reqState === "Delivery In-Progress" ||
            d.reqState === "Give Feedback")
        )
          return false;
        return true;
      })
      .map((d) => normalizeAppOrder(d, "order"));

    // Normalize and merge completed in-app orders from 'oldOrder'.
    // s_delete is a user-facing soft-delete flag; admins see ALL historical records.
    const appOldOrdersMapped = appOldOrderDocs
      .filter((d) => {
        if (d.reqState === "Order Cancelled") return false;
        if (
          !showOldOrders &&
          (d.reqState === "Delivery In-Progress" ||
            d.reqState === "Give Feedback")
        )
          return false;
        return true;
      })
      .map((d) => normalizeAppOrder(d, "oldOrder"));
    const appOldOrders = showOldOrders
      ? appOldOrdersMapped
      : appOldOrdersMapped.filter((o) => {
          if (!o.deliveryDate) return false;
          const d = new Date(o.deliveryDate + "T00:00:00");
          return !isNaN(d.getTime()) && d >= todayForFilter;
        });

    let filtered = [...formsOrders, ...appOrders, ...appOldOrders];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((order) =>
        Object.values(order).some((value) => {
          if (Array.isArray(value)) {
            return value.some((v) => {
              if (v && typeof v === "object" && !Array.isArray(v)) {
                return Object.values(v).some(
                  (subVal) => subVal && String(subVal).toLowerCase().includes(q)
                );
              }
              return v && String(v).toLowerCase().includes(q);
            });
          } else if (value && typeof value === "object") {
            return Object.values(value).some(
              (subVal) => subVal && String(subVal).toLowerCase().includes(q)
            );
          }
          return (
            value !== undefined &&
            value !== null &&
            String(value).toLowerCase().includes(q)
          );
        })
      );
    }

    // Sort within each month by delivery date, respecting sortConfig direction
    const sortDir =
      sortConfig.key === "deliveryDate" ? sortConfig.direction : "asc";
    filtered.sort((a, b) => {
      const dateA = a.deliveryDate
        ? new Date(convertDateFormat(a.deliveryDate))
        : new Date(0);
      const dateB = b.deliveryDate
        ? new Date(convertDateFormat(b.deliveryDate))
        : new Date(0);
      return sortDir === "asc" ? dateA - dateB : dateB - dateA;
    });

    const groupedByMonth = {};

    filtered.forEach((order) => {
      const dateStr = order.deliveryDate;
      // For app orders without a date, group under current month rather than drop them
      let date;
      if (!dateStr) {
        if (!order._isAppOrder) return; // drop Forms orders without date
        date = new Date(); // fallback: current month for dateless app orders
      } else {
        date = new Date(convertDateFormat(dateStr));
        if (isNaN(date.getTime())) return;
      }

      const monthKey = date.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });

      if (!groupedByMonth[monthKey]) {
        groupedByMonth[monthKey] = [];
      }

      groupedByMonth[monthKey].push(order);
    });

    // Sort months in DESCENDING order (most recent first)
    const sortedMonths = Object.keys(groupedByMonth).sort((a, b) => {
      // Parse month and year from the string
      const parseMonthYear = (str) => {
        const [month, year] = str.split(" ");
        const monthIndex = new Date(`${month} 1, 2000`).getMonth();
        return new Date(year, monthIndex);
      };

      const dateA = parseMonthYear(a);
      const dateB = parseMonthYear(b);
      return dateB - dateA; // Changed to descending
    });

    const finalGrouped = {};
    sortedMonths.forEach((month) => {
      finalGrouped[month] = groupedByMonth[month];
    });

    return finalGrouped;
  };

  const renderFormsView = () => {
    const groupedOrders = applyFormsFiltersAndGrouping();
    const months = Object.keys(groupedOrders);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div className="order-page">
        <div className="order-forms-view">
          <div className="order-forms-controls">
            <button
              onClick={handleSyncForms}
              className="order-refresh-btn"
              title="Sync Forms Data"
              disabled={loadingForms}
            >
              <FaSync
                className={loadingForms ? "order-refresh-spinning" : ""}
              />
            </button>

            <div className="order-filter-section">
              <input
                type="text"
                className="order-filter-input"
                placeholder="Search by Name, Email, Phone, or Flavour..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              {searchQuery && (
                <button
                  className="order-search-clear-btn"
                  onClick={handleClearSearch}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <button
              className={`order-data-source-toggle ${
                showOldOrders ? "" : "off"
              }`}
              onClick={() => setShowOldOrders(!showOldOrders)}
              title={
                showOldOrders
                  ? "Showing all orders"
                  : "Showing current/future orders only"
              }
            >
              {showOldOrders ? <FaToggleOn /> : <FaToggleOff />}
            </button>
          </div>

          {driveError && <p className="order-error-message">{driveError}</p>}

          {formsError && <p className="order-error-message">{formsError}</p>}

          {loadingForms && (
            <p className="order-loading-message">Loading order data...</p>
          )}

          {customerModal && (
            <CustomerModal
              order={customerModal}
              userRole={userRole}
              user={user}
              db={db}
              formsOrders={formsOrders}
              setFormsOrders={setFormsOrders}
              priceInput={priceInput}
              setPriceInput={setPriceInput}
              priceLoading={priceLoading}
              setPriceLoading={setPriceLoading}
              deliveryDateInput={deliveryDateInput}
              setDeliveryDateInput={setDeliveryDateInput}
              deliveryDateLoading={deliveryDateLoading}
              setDeliveryDateLoading={setDeliveryDateLoading}
              deliveryTimeInput={deliveryTimeInput}
              setDeliveryTimeInput={setDeliveryTimeInput}
              deliveryTimeLoading={deliveryTimeLoading}
              setDeliveryTimeLoading={setDeliveryTimeLoading}
              holidayCalendar={orderHolidayCalendar}
              timeWindows={orderTimeWindows}
              onClose={closeCustomerModal}
              convertDateFormat={convertDateFormat}
              normalizePhoneNumber={normalizePhoneNumber}
              userProfileMap={userProfileMap}
            />
          )}

          {tierModal && (
            <div
              className="order-customer-modal-overlay"
              onClick={closeTierModal}
            >
              <div
                className="order-customer-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="order-customer-modal-close-btn"
                  onClick={closeTierModal}
                  title="Close"
                >
                  ×
                </button>
                <div className="order-customer-modal-details">
                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">Cover</span>
                    <div className="order-customer-detail-value">
                      {(userRole === "Admin" || userRole === "Super") &&
                      isNewOrder(tierModal) ? (
                        <input
                          type="text"
                          value={tierCoverInput}
                          onChange={(e) => setTierCoverInput(e.target.value)}
                          onBlur={saveTierCover}
                          disabled={tierCoverLoading}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "14px",
                            outline: "none",
                            padding: "2px 4px",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <span>{tierModal.frosting || "N/A"}</span>
                      )}
                      {tierCoverLoading && (
                        <span
                          style={{
                            marginLeft: "4px",
                            fontSize: "10px",
                            color: "#999",
                          }}
                        >
                          Saving...
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="order-customer-detail-row">
                    <span
                      className="order-customer-detail-label"
                      style={{ fontWeight: "bold", color: "#333" }}
                    >
                      Tier 1
                    </span>
                    <span className="order-customer-detail-value"></span>
                  </div>

                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">Sponge</span>
                    <div className="order-customer-detail-value">
                      {(userRole === "Admin" || userRole === "Super") &&
                      isNewOrder(tierModal) ? (
                        <input
                          type="text"
                          value={tierSpongeBaseInput}
                          onChange={(e) =>
                            setTierSpongeBaseInput(e.target.value)
                          }
                          onBlur={() => saveTierSpongeBase(1)}
                          disabled={tierSpongeBaseLoading}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "14px",
                            outline: "none",
                            padding: "2px 4px",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <span>{tierModal.flavour || "N/A"}</span>
                      )}
                      {tierSpongeBaseLoading && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "12px",
                            color: "#999",
                          }}
                        >
                          Saving...
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">
                      Cake Flavour
                    </span>
                    <div className="order-customer-detail-value">
                      {(userRole === "Admin" || userRole === "Super") &&
                      isNewOrder(tierModal) ? (
                        <input
                          type="text"
                          value={tierCakeFlavourInput}
                          onChange={(e) =>
                            setTierCakeFlavourInput(e.target.value)
                          }
                          onBlur={() => saveTierCakeFlavour(1)}
                          disabled={tierCakeFlavourLoading}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "14px",
                            outline: "none",
                            padding: "2px 4px",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <span>{tierModal.filling || "N/A"}</span>
                      )}
                      {tierCakeFlavourLoading && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "12px",
                            color: "#999",
                          }}
                        >
                          Saving...
                        </span>
                      )}
                    </div>
                  </div>

                  {tierModal.tierCount >= 2 && (
                    <>
                      <div className="order-customer-detail-row">
                        <span
                          className="order-customer-detail-label"
                          style={{ fontWeight: "bold", color: "#333" }}
                        >
                          Tier 2
                        </span>
                        <span className="order-customer-detail-value"></span>
                      </div>

                      <div className="order-customer-detail-row">
                        <span className="order-customer-detail-label">
                          Sponge
                        </span>
                        <div className="order-customer-detail-value">
                          {(userRole === "Admin" || userRole === "Super") &&
                          isNewOrder(tierModal) ? (
                            <input
                              type="text"
                              value={tierSpongeBase2Input}
                              onChange={(e) =>
                                setTierSpongeBase2Input(e.target.value)
                              }
                              onBlur={() => saveTierSpongeBase(2)}
                              disabled={tierSpongeBase2Loading}
                              style={{
                                border: "none",
                                background: "transparent",
                                fontSize: "14px",
                                outline: "none",
                                padding: "2px 4px",
                                width: "100%",
                              }}
                            />
                          ) : (
                            <span>{tierModal.spongeFlavour2 || "N/A"}</span>
                          )}
                          {tierSpongeBase2Loading && (
                            <span
                              style={{
                                marginLeft: "4px",
                                fontSize: "10px",
                                color: "#999",
                              }}
                            >
                              Saving...
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="order-customer-detail-row">
                        <span className="order-customer-detail-label">
                          Cake Flavour
                        </span>
                        <div className="order-customer-detail-value">
                          {(userRole === "Admin" || userRole === "Super") &&
                          isNewOrder(tierModal) ? (
                            <input
                              type="text"
                              value={tierCakeFlavour2Input}
                              onChange={(e) =>
                                setTierCakeFlavour2Input(e.target.value)
                              }
                              onBlur={() => saveTierCakeFlavour(2)}
                              disabled={tierCakeFlavour2Loading}
                              style={{
                                border: "none",
                                background: "transparent",
                                fontSize: "14px",
                                outline: "none",
                                padding: "2px 4px",
                                width: "100%",
                              }}
                            />
                          ) : (
                            <span>{tierModal.filling2 || "N/A"}</span>
                          )}
                          {tierCakeFlavour2Loading && (
                            <span
                              style={{
                                marginLeft: "4px",
                                fontSize: "10px",
                                color: "#999",
                              }}
                            >
                              Saving...
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {tierModal.tierCount >= 3 && (
                    <>
                      <div className="order-customer-detail-row">
                        <span
                          className="order-customer-detail-label"
                          style={{ fontWeight: "bold", color: "#333" }}
                        >
                          Tier 3
                        </span>
                        <span className="order-customer-detail-value"></span>
                      </div>

                      <div className="order-customer-detail-row">
                        <span className="order-customer-detail-label">
                          Sponge
                        </span>
                        <div className="order-customer-detail-value">
                          {(userRole === "Admin" || userRole === "Super") &&
                          isNewOrder(tierModal) ? (
                            <input
                              type="text"
                              value={tierSpongeBase3Input}
                              onChange={(e) =>
                                setTierSpongeBase3Input(e.target.value)
                              }
                              onBlur={() => saveTierSpongeBase(3)}
                              disabled={tierSpongeBase3Loading}
                              style={{
                                border: "none",
                                background: "transparent",
                                fontSize: "14px",
                                outline: "none",
                                padding: "2px 4px",
                                width: "100%",
                              }}
                            />
                          ) : (
                            <span>{tierModal.spongeFlavour3 || "N/A"}</span>
                          )}
                          {tierSpongeBase3Loading && (
                            <span
                              style={{
                                marginLeft: "4px",
                                fontSize: "10px",
                                color: "#999",
                              }}
                            >
                              Saving...
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="order-customer-detail-row">
                        <span className="order-customer-detail-label">
                          Cake Flavour
                        </span>
                        <div className="order-customer-detail-value">
                          {(userRole === "Admin" || userRole === "Super") &&
                          isNewOrder(tierModal) ? (
                            <input
                              type="text"
                              value={tierCakeFlavour3Input}
                              onChange={(e) =>
                                setTierCakeFlavour3Input(e.target.value)
                              }
                              onBlur={() => saveTierCakeFlavour(3)}
                              disabled={tierCakeFlavour3Loading}
                              style={{
                                border: "none",
                                background: "transparent",
                                fontSize: "14px",
                                outline: "none",
                                padding: "2px 4px",
                                width: "100%",
                              }}
                            />
                          ) : (
                            <span>{tierModal.filling3 || "N/A"}</span>
                          )}
                          {tierCakeFlavour3Loading && (
                            <span
                              style={{
                                marginLeft: "4px",
                                fontSize: "10px",
                                color: "#999",
                              }}
                            >
                              Saving...
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {moreModal && (
            <div
              className="order-customer-modal-overlay"
              onClick={closeMoreModal}
            >
              <div
                className="order-customer-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="order-customer-modal-close-btn"
                  onClick={closeMoreModal}
                  title="Close"
                >
                  ×
                </button>
                <div className="order-customer-modal-details">
                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">
                      Name on Cake
                    </span>
                    <span className="order-customer-detail-value">
                      {safeStr(moreModal.cakeName)}
                    </span>
                  </div>

                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">Message</span>
                    <span className="order-customer-detail-value">
                      {safeStr(moreModal.message)}
                    </span>
                  </div>

                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">
                      Position
                    </span>
                    <span className="order-customer-detail-value">
                      {safeStr(moreModal.position)}
                    </span>
                  </div>

                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">
                      Special Request
                    </span>
                    <span className="order-customer-detail-value">
                      {safeStr(moreModal.specialRequest)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="order-tables-scroll-container">
            {months.map((monthKey) => {
              const orders = groupedOrders[monthKey];

              return (
                <div key={monthKey} className="order-month-group">
                  <div className="order-month-header">
                    <h3 className="order-month-title">{monthKey}</h3>
                    <button
                      className="order-toggle-btn"
                      onClick={() => toggleMonthGroup(monthKey)}
                    >
                      {expandedMonths.has(monthKey) ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )}
                    </button>
                  </div>

                  {expandedMonths.has(monthKey) && (
                    <div className="order-month-content">
                      <table className="order-order-table">
                        <thead>
                          <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th
                              className="order-sortable-header"
                              onClick={() => handleSort("deliveryDate")}
                            >
                              <div className="order-sortable-header-inner">
                                Delivery Date
                                {sortConfig.key === "deliveryDate" &&
                                  (sortConfig.direction === "asc" ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  ))}
                                {sortConfig.key !== "deliveryDate" && <FaSort />}
                              </div>
                            </th>
                            <th>Tier</th>
                            <th>Sponge</th>
                            <th>Type</th>
                            <th>Flavour</th>
                            <th>Quantity</th>
                            <th>Delivery</th>
                            <th>More</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => {
                            const deliveryDate = order.deliveryDate
                              ? new Date(order.deliveryDate)
                              : null;
                            const isToday =
                              deliveryDate &&
                              deliveryDate.getDate() === today.getDate() &&
                              deliveryDate.getMonth() === today.getMonth() &&
                              deliveryDate.getFullYear() ===
                                today.getFullYear();

                            const isOnHold = order.reqState === "Order On-hold";
                            const isPastDue =
                              order._sourceCollection === "order" &&
                              deliveryDate &&
                              !isToday &&
                              deliveryDate < today;

                            return (
                              <tr
                                key={order.id}
                                className={`${
                                  isToday ? "order-today-row" : ""
                                } ${isPastDue ? "order-pastdue-row" : ""} ${
                                  isOnHold ? "order-onhold-row" : ""
                                }`.trim()}
                              >
                                <td>
                                  {order.images && order.images[0] ? (
                                    <img
                                      src={processImageUrl(order.images[0])}
                                      alt="Order"
                                      className="order-order-image"
                                      loading="lazy"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const processedUrl = processImageUrl(
                                          order.images[0]
                                        );
                                        if (processedUrl) {
                                          setSelectedImageUrl(processedUrl);
                                          setZoomModalOpen(true);
                                        }
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <></>
                                  )}
                                </td>
                                <td>
                                  <div
                                    className="order-name-cell"
                                    onClick={() => handleOrderClick(order)}
                                  >
                                    <span style={userProfileMap[order.userId]?.env === 'Test' ? { color: 'orange' } : {}}>
                                      {order.name || "N/A"}
                                    </span>
                                    {isOnHold && (
                                      <span
                                        style={{
                                          marginLeft: "6px",
                                          color: "#fa4848",
                                          fontWeight: 600,
                                        }}
                                      >
                                        (on Hold)
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>{formatDate(order.deliveryDate)}</td>
                                <td
                                  className="order-tier-cell"
                                  onClick={() => handleTierClick(order)}
                                >
                                  {order.tierCount || "N/A"}
                                </td>
                                <td>{order.flavour || "N/A"}</td>
                                <td>
                                  {(userRole === "Admin" ||
                                    userRole === "Super") &&
                                  isNewOrder(order) &&
                                  editingSpongeTypeId === order.id ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <select
                                        autoFocus
                                        value={spongeTypeInput}
                                        onChange={(e) => {
                                          setSpongeTypeInput(e.target.value);
                                          saveSpongeType(
                                            order.id,
                                            e.target.value
                                          );
                                        }}
                                        onBlur={() =>
                                          setEditingSpongeTypeId(null)
                                        }
                                        disabled={spongeTypeLoading}
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          fontSize: "14px",
                                          outline: "none",
                                          padding: "2px 4px",
                                          cursor: "pointer",
                                          width: "100%",
                                        }}
                                      >
                                        <option value="">Select Type</option>
                                        <option value="With Egg">
                                          With Egg
                                        </option>
                                        <option value="Eggless">Eggless</option>
                                      </select>
                                      {spongeTypeLoading && (
                                        <span
                                          style={{
                                            marginLeft: "4px",
                                            fontSize: "10px",
                                            color: "#999",
                                          }}
                                        >
                                          Saving...
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span
                                      onClick={() => {
                                        if (
                                          (userRole === "Admin" ||
                                            userRole === "Super") &&
                                          isNewOrder(order)
                                        ) {
                                          setEditingSpongeTypeId(order.id);
                                        }
                                      }}
                                      title={
                                        (userRole === "Admin" ||
                                          userRole === "Super") &&
                                        isNewOrder(order)
                                          ? "Click to change type"
                                          : !isNewOrder(order)
                                          ? "Cannot edit past orders"
                                          : ""
                                      }
                                      style={{
                                        cursor:
                                          (userRole === "Admin" ||
                                            userRole === "Super") &&
                                          isNewOrder(order)
                                            ? "pointer"
                                            : "default",
                                        color: "inherit",
                                      }}
                                    >
                                      {order.spongeType || "N/A"}
                                    </span>
                                  )}
                                </td>
                                <td>{order.filling || "N/A"}</td>
                                <td>
                                  {(userRole === "Admin" ||
                                    userRole === "Super") &&
                                  isNewOrder(order) &&
                                  editingQuantityId === order.id ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <input
                                        type="text"
                                        value={quantityInput}
                                        onChange={(e) => {
                                          setQuantityInput(e.target.value);
                                          saveQuantity(
                                            order.id,
                                            e.target.value
                                          );
                                        }}
                                        onBlur={() =>
                                          setEditingQuantityId(null)
                                        }
                                        disabled={quantityLoading}
                                        autoFocus
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          fontSize: "14px",
                                          outline: "none",
                                          padding: "2px 4px",
                                          width: "100%",
                                        }}
                                      />
                                      {quantityLoading && (
                                        <span
                                          style={{
                                            marginLeft: "4px",
                                            fontSize: "10px",
                                            color: "#999",
                                          }}
                                        >
                                          Saving...
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span
                                      onClick={() => {
                                        if (
                                          (userRole === "Admin" ||
                                            userRole === "Super") &&
                                          isNewOrder(order)
                                        ) {
                                          setEditingQuantityId(order.id);
                                        }
                                      }}
                                      title={
                                        (userRole === "Admin" ||
                                          userRole === "Super") &&
                                        isNewOrder(order)
                                          ? "Click to change quantity"
                                          : !isNewOrder(order)
                                          ? "Cannot edit past orders"
                                          : ""
                                      }
                                      style={{
                                        cursor:
                                          (userRole === "Admin" ||
                                            userRole === "Super") &&
                                          isNewOrder(order)
                                            ? "pointer"
                                            : "default",
                                        color: "inherit",
                                      }}
                                    >
                                      {order.quantity
                                        ? (order._sourceCollection === "FormOrders" || order._sourceCollection === "FormOrderOld")
                                          ? String(order.quantity).replace(/\s*\([^)]*\)/, "").trim() || String(order.quantity)
                                          : `${String(order.quantity).replace(/\s*kg$/i, '').trim()} Kilogram`
                                        : "N/A"}
                                    </span>
                                  )}
                                </td>
                                <td>{order.del_mode ? "Yes" : "No"}</td>
                                <td
                                  className="order-more-cell"
                                  onClick={() => handleMoreClick(order)}
                                >
                                  ....
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {months.length === 0 && !loadingForms && (
              <p className="order-no-match-message">
                {showOldOrders
                  ? "No orders found."
                  : "No orders found for current and future dates."}
              </p>
            )}
          </div>

          {/* Image zoom modal */}
          <ImageZoomModal
            isOpen={zoomModalOpen}
            onRequestClose={() => setZoomModalOpen(false)}
            imageUrl={selectedImageUrl}
          />
        </div>
      </div>
    );
  };

  if (!user) {
    return <p>Please log-in to view your cart.</p>;
  }

  if (loadingForms) {
    return <p>Loading order data...</p>;
  }

  return renderFormsView();
};

// Separate Customer Modal Component for cleaner code
const CustomerModal = ({
  order,
  userRole,
  user,
  db,
  formsOrders,
  setFormsOrders,
  priceInput,
  setPriceInput,
  priceLoading,
  setPriceLoading,
  deliveryDateInput,
  setDeliveryDateInput,
  deliveryDateLoading,
  setDeliveryDateLoading,
  deliveryTimeInput,
  setDeliveryTimeInput,
  deliveryTimeLoading,
  setDeliveryTimeLoading,
  holidayCalendar,
  timeWindows,
  onClose,
  convertDateFormat,
  normalizePhoneNumber,
  userProfileMap,
}) => {
  const name = order.name || "N/A";
  const email = order.email || "N/A";
  const phone = order.phone || "N/A";
  const address = order.address || "N/A";
  const deliveryDate = order.deliveryDate || "N/A";
  const eventDate = order.eventDate;
  const isPickup = !order.del_mode;

  const [expandedCurrent, setExpandedCurrent] = useState(true);
  const [expandedOlderOrders, setExpandedOlderOrders] = useState(false);
  const [olderOrders, setOlderOrders] = useState([]);
  const [loadingOlderOrders, setLoadingOlderOrders] = useState(false);
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");

  // Shop feedback state
  const [showShopFeedbackModal, setShowShopFeedbackModal] = useState(false);
  const [shopFeedbackRating, setShopFeedbackRating] = useState(0);
  const [shopFeedbackHover, setShopFeedbackHover] = useState(0);
  const [shopFeedbackComment, setShopFeedbackComment] = useState("");
  const [shopFeedbackSaving, setShopFeedbackSaving] = useState(false);
  const [currentShopFeedback, setCurrentShopFeedback] = useState(order.shopfeedback || null);

  const processImageUrl = (imageUrlOrFileId) => {
    if (!imageUrlOrFileId) return null;

    // If it's already a processed proxy URL, return as is
    if (imageUrlOrFileId.includes("cloudfunctions.net/driveImageProxy")) {
      return imageUrlOrFileId;
    }

    let processedUrl = imageUrlOrFileId;

    // If it's a Google Drive URL or file ID, convert it to proxy URL
    if (
      imageUrlOrFileId.includes("drive.google.com") ||
      (!imageUrlOrFileId.includes("http") && !imageUrlOrFileId.includes("//"))
    ) {
      // Extract file ID from Google Drive URL if needed
      let fileId = imageUrlOrFileId;
      if (imageUrlOrFileId.includes("drive.google.com")) {
        const match = imageUrlOrFileId.match(/[-\w]{25,}/);
        fileId = match ? match[0] : imageUrlOrFileId;
      }

      // Convert to proxy URL
      const proxyBaseUrl =
        import.meta.env.VITE_DRIVE_PROXY_URL ||
        "https://us-central1-pcakedatastore.cloudfunctions.net/driveImageProxy";
      processedUrl = `${proxyBaseUrl}?id=${fileId}&sz=w800`;
    }

    // Validate the URL
    if (validateImageUrl(processedUrl)) {
      return processedUrl;
    }

    return null;
  };

  const validateImageUrl = (url) => {
    if (!url) return false;
    if (typeof url !== "string") return false;

    // Skip if it's not a proper URL or doesn't start with expected patterns
    if (!url.startsWith("http") && !url.startsWith("//")) {
      return false;
    }

    // Skip Google Forms URLs
    if (url.includes("forms_web") || url.includes("usp=forms")) {
      return false;
    }

    // Accept both proxy URLs and direct URLs
    return true;
  };

  const formatDate = (d) => {
    if (!d) return "N/A";
    try {
      let dateStr = d;
      // Convert DD.MM.YYYY to YYYY-MM-DD
      if (dateStr.includes(".")) {
        const [day, month, year] = dateStr.split(".");
        dateStr = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
      const date = new Date(dateStr + "T00:00:00");

      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };

  const handleShopFeedbackSubmit = async () => {
    if (shopFeedbackRating === 0) return;
    setShopFeedbackSaving(true);
    try {
      const docRef = doc(db, order._sourceCollection || "FormOrders", order.id);
      const feedbackData = {
        rating: shopFeedbackRating,
        comment: shopFeedbackComment.trim() || null,
        submittedAt: serverTimestamp(),
      };
      await setDoc(docRef, { shopfeedback: feedbackData }, { merge: true });
      setCurrentShopFeedback({ ...feedbackData, submittedAt: new Date() });
      setShowShopFeedbackModal(false);
      setShopFeedbackRating(0);
      setShopFeedbackHover(0);
      setShopFeedbackComment("");
    } catch (e) {
      console.error("Error saving shop feedback:", e);
    } finally {
      setShopFeedbackSaving(false);
    }
  };

  const openShopFeedbackModal = () => {
    setShopFeedbackRating(currentShopFeedback?.rating || 0);
    setShopFeedbackHover(0);
    setShopFeedbackComment(currentShopFeedback?.comment || "");
    setShowShopFeedbackModal(true);
  };

  const cleanQuantity = (quantityStr) => {
    if (!quantityStr || quantityStr === "N/A") return "N/A";

    // Remove anything in parentheses
    const cleaned = quantityStr.replace(/\s*\([^)]*\)/, "").trim();

    // If empty after cleaning, return original
    if (!cleaned) return quantityStr;

    return cleaned;
  };

  // Fetch older orders function
  const fetchOlderOrders = useCallback(async () => {
    setLoadingOlderOrders(true);

    try {
      // If this is an app order with a userId, also search order/oldOrder by userId
      if (order.userId) {
        const [snapOrder, snapOldOrder] = await Promise.all([
          getDocs(
            query(collection(db, "order"), where("userId", "==", order.userId))
          ),
          getDocs(
            query(
              collection(db, "oldOrder"),
              where("userId", "==", order.userId)
            )
          ),
        ]);
        const appHistoryDocs = [...snapOrder.docs, ...snapOldOrder.docs].filter(
          (d) => d.id !== order.id && d.data().reqState !== "Order Cancelled"
        ); // exclude current order and cancelled orders
        if (appHistoryDocs.length > 0) {
          const appHistory = appHistoryDocs.map((d) => {
            const data = d.data();
            const firstImage =
              Array.isArray(data.images) && data.images[0]
                ? data.images[0]
                : null;
            const rawDate = data.deliveryDate;
            let dateStr = "";
            if (rawDate) {
              if (
                typeof rawDate === "object" &&
                typeof rawDate.toDate === "function"
              ) {
                const dt = rawDate.toDate();
                dateStr = `${dt.getFullYear()}-${String(
                  dt.getMonth() + 1
                ).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
              } else {
                dateStr = String(rawDate);
              }
            }
            const firstTier = (data.tiers && data.tiers[0]) || {};
            return {
              id: d.id,
              image: firstImage,
              name: order.name || "",
              deliveryDate: dateStr,
              sponge: data.sponge || firstTier.sponge || "",
              spongeType:
                data.eggless === true
                  ? "Eggless"
                  : data.eggless === false
                  ? "With Egg"
                  : data.cfilling || firstTier.cfilling || "",
              flavour: data.sponge || firstTier.sponge || "",
              filling: data.cflavour || firstTier.cflavour || "",
              cover: data.cover || data.frosting || "",
              quantity: data.quantity ? String(data.quantity) : "",
              price: (() => {
                const cake =
                  data.ccost != null ? parseFloat(String(data.ccost)) : null;
                const del =
                  data.del_cost != null
                    ? parseFloat(String(data.del_cost))
                    : null;
                if (cake != null) {
                  const total = del != null ? cake + del : cake;
                  return `${total} ${getCurrencySymbol()}`;
                }
                return data.Cost || "";
              })(),
              matchedBy: "userId",
              actualEmail: order.email || "",
              actualPhone: order.phone || "",
              actualAddress: data.del_address || order.address || "",
              shopfeedback: data.shopfeedback || null,
            };
          });
          appHistory.sort((a, b) => {
            if (!a.deliveryDate) return 1;
            if (!b.deliveryDate) return -1;
            return new Date(b.deliveryDate) - new Date(a.deliveryDate);
          });
          setOlderOrders(appHistory);
          return;
        }
      }

      // Helper to check if a value is valid for searching
      const isValidForSearch = (value, type) => {
        if (!value || typeof value !== "string") return false;

        const trimmed = value.trim();
        if (
          trimmed === "" ||
          trimmed === "N/A" ||
          trimmed === "null" ||
          trimmed === "undefined"
        ) {
          return false;
        }

        // Type-specific validation
        if (type === "email") {
          // Basic email validation - must contain @ and .
          return trimmed.includes("@") && trimmed.includes(".");
        } else if (type === "phone") {
          // Must contain at least 5 digits (for phone validation)
          const digits = trimmed.replace(/\D/g, "");
          return digits.length >= 5;
        } else if (type === "address") {
          // Strip dashes before checking – a dash-only value (e.g. "-") is not a real address.
          // Must have at least 3 meaningful characters after removing dashes.
          return trimmed.replace(/-/g, "").trim().length >= 3;
        }

        return true;
      };

      // Normalize German street abbreviations to a canonical form for comparison
      const normalizeAddress = (str) => {
        if (!str) return "";
        return str
          .toLowerCase()
          .replace(/stra(?:ß|ss)e/g, "str")
          .replace(/str\./g, "str")
          .replace(/\bstr\b/g, "str")
          .replace(/[-]/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      };

      // Return address variants covering common German street abbreviation spellings
      // so Firestore exact-match queries can find records regardless of which form was stored
      const getAddressVariants = (addr) => {
        if (!addr) return [addr];
        const variants = new Set([addr]);

        // Also add a hyphen-normalized version (dashes → spaces) as an additional base
        const dashNormalized = addr
          .replace(/-/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (dashNormalized !== addr) variants.add(dashNormalized);

        // Also add a street-only base by stripping leading numeric tokens (zip code / house number).
        // Handles cases where one order stores "60486 4 Melli-Beese-Str" and another stores
        // just "Melli Beese Strasse" — the street name is the same despite the different formats.
        const streetOnly = addr
          .trim()
          .replace(/^(\s*\d+\s+)+/, "")
          .trim();
        if (streetOnly && streetOnly !== addr.trim()) variants.add(streetOnly);
        // Also dash-normalize the street-only form
        const streetOnlyDash = streetOnly
          .replace(/-/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (streetOnlyDash && streetOnlyDash !== streetOnly)
          variants.add(streetOnlyDash);

        // Each replacement includes both capitalized and lowercase forms so Firestore
        // exact-match queries work regardless of how the suffix was stored.
        // Deduplication via normalizeAddress prevents redundant Firestore queries.
        const pairs = [
          [/straße/gi, ["Strasse", "strasse", "Str.", "str.", "Str", "str"]],
          [/strasse/gi, ["Straße", "straße", "Str.", "str.", "Str", "str"]],
          [/str\./gi, ["Straße", "straße", "Strasse", "strasse", "Str", "str"]],
          [
            /\bstr\b(?!\.)/gi,
            ["Straße", "straße", "Strasse", "strasse", "Str.", "str."],
          ],
        ];

        // Apply all patterns to every base variant (including dash-normalized)
        const baseVariants = [...variants];
        for (const base of baseVariants) {
          for (const [pat, reps] of pairs) {
            for (const rep of reps) {
              const v = base.replace(pat, rep);
              if (v !== base) variants.add(v);
            }
          }
        }
        return [...variants].filter(Boolean);
      };

      // Normalize phone number for searching
      const normalizedCurrentPhone = normalizePhoneNumber(phone);

      // Track visited documents to avoid duplicates
      const visitedIds = new Set([order.id]);
      const allFoundOrders = new Map();

      // Track what we've searched for to avoid repeats
      const searchedEmails = new Set();
      const searchedPhones = new Set(); // For both normalized and exact
      const searchedAddresses = new Set();

      // Initial criteria to search
      const initialSearchQueue = [];

      // Add email to search if valid
      if (isValidForSearch(email, "email")) {
        initialSearchQueue.push({ type: "email", value: email });
        searchedEmails.add(email);
      }

      // Add normalized phone to search if valid
      if (isValidForSearch(normalizedCurrentPhone, "phone")) {
        initialSearchQueue.push({
          type: "phone_norm",
          value: normalizedCurrentPhone,
        });
        searchedPhones.add(normalizedCurrentPhone);
      }

      // Add exact phone to search if valid AND different from normalized
      if (
        isValidForSearch(phone, "phone") &&
        phone !== normalizedCurrentPhone
      ) {
        initialSearchQueue.push({ type: "phone_exact", value: phone });
        searchedPhones.add(phone);
      }

      // Add address variants to search – covers Straße/Strasse/Str./Str spelling differences.
      // Deduplication uses the exact variant string (not normalizeAddress) so that
      // "Melli-Beese-Str" and "Melli Beese Strasse" are both queued even though they
      // normalize to the same form – Firestore needs the exact stored value to match.
      if (isValidForSearch(address, "address")) {
        for (const variant of getAddressVariants(address)) {
          if (
            isValidForSearch(variant, "address") &&
            !searchedAddresses.has(variant)
          ) {
            initialSearchQueue.push({ type: "address", value: variant });
            searchedAddresses.add(variant);
          }
        }
      }

      // BFS search through related orders.
      // Phase 1: run ALL initial queries (no cap) so every address variant is tried.
      // Phase 2: BFS expansion from found docs is capped to prevent runaway chains.
      let searchQueue = [...initialSearchQueue];
      let bfsExpansionCount = 0;
      const MAX_BFS_EXPANSIONS = 10; // limit on newly-discovered criteria, not initial ones
      const initialCount = initialSearchQueue.length;
      let processedCount = 0;

      while (searchQueue.length > 0) {
        // Stop BFS-expanded items once cap is reached; always process initial items
        if (
          processedCount >= initialCount &&
          bfsExpansionCount >= MAX_BFS_EXPANSIONS
        )
          break;
        processedCount++;
        const currentItem = searchQueue.shift();
        const { type, value } = currentItem;

        // Build the Firestore field name for this search type
        let firestoreField;
        switch (type) {
          case "email":
            firestoreField = "E-Mail-Address";
            break;
          case "phone_norm":
            firestoreField = "NormalizedPhone";
            break;
          case "phone_exact":
            firestoreField = "Local Phone Number";
            break;
          case "address":
            firestoreField = "Address";
            break;
          default:
            continue;
        }

        // Query both collections and merge results (deduplication handled by visitedIds)
        const [snapNew, snapOld] = await Promise.all([
          getDocs(
            query(
              collection(db, "FormOrders"),
              where(firestoreField, "==", value)
            )
          ),
          getDocs(
            query(
              collection(db, "FormOrderOld"),
              where(firestoreField, "==", value)
            )
          ),
        ]);
        const combinedDocs = [...snapNew.docs, ...snapOld.docs];
        // Wrap in a snapshot-like object so the rest of the loop is unchanged
        const querySnapshot = { docs: combinedDocs };

        // Process results
        const newItemsToSearch = [];

        for (const doc of querySnapshot.docs) {
          if (visitedIds.has(doc.id)) {
            continue; // Already processed
          }

          visitedIds.add(doc.id);
          const data = doc.data();

          let image = "";
          const rawImageString =
            data["Upload Wished Design (Max 10 MB or 5 Files)"] || "";
          if (rawImageString) {
            const imageUrls = rawImageString
              .split(",")
              .map((url) => url.trim())
              .filter((url) => url);
            if (imageUrls.length > 0) {
              const firstImageUrl = imageUrls[0];
              // Use the reusable function
              const processedImageUrl = processImageUrl(firstImageUrl);
              if (processedImageUrl) {
                image = processedImageUrl;
              }
            }
          }

          // Extract fields
          const docEmail = (data["E-Mail-Address"] || "").trim();
          const docPhone = (data["Local Phone Number"] || "").trim();
          const docNormPhone = (data["NormalizedPhone"] || "").trim();
          const docAddress = (data["Address"] || "").trim();

          // Check if this document actually matches the query meaningfully
          // (not just on empty/null values)
          let isMeaningfulMatch = false;

          if (
            type === "email" &&
            docEmail === value &&
            isValidForSearch(docEmail, "email")
          ) {
            isMeaningfulMatch = true;
          } else if (
            type === "phone_norm" &&
            docNormPhone === value &&
            isValidForSearch(docNormPhone, "phone")
          ) {
            isMeaningfulMatch = true;
          } else if (
            type === "phone_exact" &&
            docPhone === value &&
            isValidForSearch(docPhone, "phone")
          ) {
            isMeaningfulMatch = true;
          } else if (
            type === "address" &&
            normalizeAddress(docAddress) === normalizeAddress(value) &&
            isValidForSearch(docAddress, "address")
          ) {
            isMeaningfulMatch = true;
          }

          if (!isMeaningfulMatch) {
            // Skip this document - it doesn't meaningfully match
            visitedIds.delete(doc.id);
            continue;
          }

          // Add to results
          const _rawQty = data["Quantity (Kilo / # Person)"] || "";
          const _rawOtherQty = data["Other Quantity (Kilogram)"] || "";
          let _processedQty = "";
          if (
            _rawQty &&
            (_rawQty.includes("More/Other") || _rawQty.includes("Other"))
          ) {
            if (_rawOtherQty) {
              const _numMatch = _rawOtherQty.match(/(\d+\.?\d*)/);
              _processedQty = _numMatch
                ? `${_numMatch[1]} Kilogram`
                : `${_rawOtherQty} Kilogram`;
            } else {
              _processedQty = "Other Quantity";
            }
          } else if (_rawQty) {
            _processedQty = _rawQty.replace(/\s*\([^)]*\)$/, "");
          }
          allFoundOrders.set(doc.id, {
            id: doc.id,
            image: image,
            name: data["Contact Person Full Name"] || "",
            deliveryDate: data["Delivery/Pick-Up Date"] || "",
            sponge: data["Sponge Flavour"] || "",
            spongeType: data["Sponge Type"] || "",
            flavour: data["Inside Flavour/Filling"] || "",
            filling: data["Inside Flavour/Filling"] || "",
            cover: data["Outside Cover/Frosting"] || "",
            quantity: _processedQty,
            price: data["Cost"] || "",
            _autoGenerated: data["_autoGenerated"] || false,
            matchedBy: type,
            // Store the actual values for reference
            actualEmail: docEmail,
            actualPhone: docPhone,
            actualAddress: docAddress,
            shopfeedback: data.shopfeedback || null,
          });

          // Collect NEW searchable criteria from this document
          // Only add criteria that are:
          // 1. Valid for search
          // 2. Not already searched
          // 3. Different from what we originally searched for

          // Email
          if (
            isValidForSearch(docEmail, "email") &&
            !searchedEmails.has(docEmail) &&
            docEmail !== email
          ) {
            newItemsToSearch.push({ type: "email", value: docEmail });
            searchedEmails.add(docEmail);
          }

          // Normalized phone
          if (
            isValidForSearch(docNormPhone, "phone") &&
            !searchedPhones.has(docNormPhone) &&
            docNormPhone !== normalizedCurrentPhone
          ) {
            newItemsToSearch.push({ type: "phone_norm", value: docNormPhone });
            searchedPhones.add(docNormPhone);
          }

          // Exact phone (if different from normalized)
          if (
            isValidForSearch(docPhone, "phone") &&
            !searchedPhones.has(docPhone) &&
            docPhone !== phone &&
            docPhone !== normalizedCurrentPhone
          ) {
            newItemsToSearch.push({ type: "phone_exact", value: docPhone });
            searchedPhones.add(docPhone);
          }

          // Address – expand to all street-abbreviation variants for BFS queries
          if (
            isValidForSearch(docAddress, "address") &&
            normalizeAddress(docAddress) !== normalizeAddress(address)
          ) {
            for (const variant of getAddressVariants(docAddress)) {
              if (
                isValidForSearch(variant, "address") &&
                !searchedAddresses.has(variant)
              ) {
                newItemsToSearch.push({ type: "address", value: variant });
                searchedAddresses.add(variant);
              }
            }
          }
        }

        // Add new items to search queue
        if (newItemsToSearch.length > 0) {
          searchQueue.push(...newItemsToSearch);
          bfsExpansionCount += newItemsToSearch.length;
        }
      }

      // Bidirectional address fallback: range query on the numeric prefix (postal code + house
      // number) so that "60486 4 Melli Beese Strasse" also finds "60486 4 Melli-Beese-Str ".
      // Exact-match variants can't reconstruct hyphenated forms from space-separated ones,
      // but a prefix range query fetches all addresses sharing the same leading numbers and
      // normalizeAddress handles the rest client-side.
      const numericPrefixMatch = address.trim().match(/^((?:\d+\s+)+)/);
      if (numericPrefixMatch && isValidForSearch(address, "address")) {
        const prefix = numericPrefixMatch[1]; // e.g. "60486 4 "
        const [pfxSnapNew, pfxSnapOld] = await Promise.all([
          getDocs(
            query(
              collection(db, "FormOrders"),
              where("Address", ">=", prefix),
              where("Address", "<=", prefix + "\uf8ff")
            )
          ),
          getDocs(
            query(
              collection(db, "FormOrderOld"),
              where("Address", ">=", prefix),
              where("Address", "<=", prefix + "\uf8ff")
            )
          ),
        ]);
        for (const doc of [...pfxSnapNew.docs, ...pfxSnapOld.docs]) {
          if (visitedIds.has(doc.id)) continue;
          const data = doc.data();
          const docAddress = (data["Address"] || "").trim();
          if (!isValidForSearch(docAddress, "address")) continue;
          if (normalizeAddress(docAddress) !== normalizeAddress(address))
            continue;
          visitedIds.add(doc.id);
          const docEmail = (data["E-Mail-Address"] || "").trim();
          const docPhone = (data["Local Phone Number"] || "").trim();
          let image = "";
          const rawImageString =
            data["Upload Wished Design (Max 10 MB or 5 Files)"] || "";
          if (rawImageString) {
            const imageUrls = rawImageString
              .split(",")
              .map((u) => u.trim())
              .filter((u) => u);
            if (imageUrls.length > 0) {
              const processedImageUrl = processImageUrl(imageUrls[0]);
              if (processedImageUrl) image = processedImageUrl;
            }
          }
          const _rawQty = data["Quantity (Kilo / # Person)"] || "";
          const _rawOtherQty = data["Other Quantity (Kilogram)"] || "";
          let _processedQty = "";
          if (
            _rawQty &&
            (_rawQty.includes("More/Other") || _rawQty.includes("Other"))
          ) {
            if (_rawOtherQty) {
              const _numMatch = _rawOtherQty.match(/(\d+\.?\d*)/);
              _processedQty = _numMatch
                ? `${_numMatch[1]} Kilogram`
                : `${_rawOtherQty} Kilogram`;
            } else {
              _processedQty = "Other Quantity";
            }
          } else if (_rawQty) {
            _processedQty = _rawQty.replace(/\s*\([^)]*\)$/, "");
          }
          allFoundOrders.set(doc.id, {
            id: doc.id,
            image,
            name: data["Contact Person Full Name"] || "",
            deliveryDate: data["Delivery/Pick-Up Date"] || "",
            sponge: data["Sponge Flavour"] || "",
            spongeType: data["Sponge Type"] || "",
            flavour: data["Inside Flavour/Filling"] || "",
            filling: data["Inside Flavour/Filling"] || "",
            cover: data["Outside Cover/Frosting"] || "",
            quantity: _processedQty,
            price: data["Cost"] || "",
            _autoGenerated: data["_autoGenerated"] || false,
            matchedBy: "address",
            actualEmail: docEmail,
            actualPhone: docPhone,
            actualAddress: docAddress,
            shopfeedback: data.shopfeedback || null,
          });
        }
      }

      // Convert results to array and sort
      const ordersArray = Array.from(allFoundOrders.values());

      ordersArray.sort((a, b) => {
        const dateA = formatDate(a.deliveryDate);
        const dateB = formatDate(b.deliveryDate);
        return new Date(dateB) - new Date(dateA); // Most recent first
      });

      setOlderOrders(ordersArray);
    } catch (error) {
      console.error("Error in fetchOlderOrders:", error);
      setOlderOrders([]);
    } finally {
      setLoadingOlderOrders(false);
    }
  }, [email, phone, address, order.id, db]);

  useEffect(() => {
    if (expandedOlderOrders) {
      fetchOlderOrders();
    }
  }, [expandedOlderOrders, fetchOlderOrders]);

  // Helper function to check if price is missing/empty
  const isPriceMissing = (order) => {
    if (!order.cost) return true;
    const trimmed = order.cost.trim();
    return trimmed === "" || trimmed === "€" || trimmed === "N/A";
  };

  const savePrice = async () => {
    if (!user || !db || !order.id || priceLoading || !priceInput.trim()) return;

    try {
      setPriceLoading(true);
      const docRef = doc(db, order._sourceCollection || "FormOrders", order.id);
      if (order._isAppOrder) {
        // The input holds ccost + del_cost total; store only the cake portion (total - del_cost) in ccost
        const inputNum = parseFloat(priceInput.trim());
        const delCostNum =
          typeof order._delCost === "number"
            ? order._delCost
            : parseFloat(order._delCost || "0") || 0;
        const ccostVal = !isNaN(inputNum)
          ? String(inputNum - delCostNum)
          : priceInput.trim();
        await setDoc(
          docRef,
          { ccost: ccostVal, updated: new Date().toISOString() },
          { merge: true }
        );
      } else {
        const priceWithEuro = `${priceInput.trim()} €`;
        await setDoc(
          docRef,
          { Cost: priceWithEuro, updated: new Date().toISOString() },
          { merge: true }
        );
        setFormsOrders(
          formsOrders.map((o) =>
            o.id === order.id ? { ...o, cost: priceWithEuro } : o
          )
        );
      }
    } catch (error) {
      console.error("Error saving price:", error);
    } finally {
      setPriceLoading(false);
    }
  };

  const saveDeliveryDate = async () => {
    if (
      !user ||
      !db ||
      !order.id ||
      deliveryDateLoading ||
      !deliveryDateInput.trim()
    )
      return;
    if (deliveryDateInput.trim() === (order.deliveryDate || "")) return;
    try {
      setDeliveryDateLoading(true);
      const newDate = deliveryDateInput.trim();
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      const isNewDatePast = new Date(newDate + "T00:00:00") < todayMidnight;

      // ── Form orders (non-app) ────────────────────────────────────────────
      // Doc IDs are built as `${email}_${deliveryDate}`, so a date change
      // always requires creating a new document with the new ID and deleting
      // the old one — in the correct collection (active vs. archive).
      if (!order._isAppOrder) {
        const sourceCollection = order._sourceCollection || "FormOrders";
        const targetCollection = isNewDatePast ? "FormOrderOld" : "FormOrders";

        // Derive new doc ID using same rule as storeAllFormsResponses
        const newDocId = order.email
          ? `${order.email}_${newDate}`.replace(/[^a-zA-Z0-9_\-@.]/g, "_")
          : order.id; // fallback: keep old id if email is missing

        const oldRef = doc(db, sourceCollection, order.id);
        const currentSnap = await getDoc(oldRef);
        if (!currentSnap.exists()) return;

        const newPayload = {
          ...currentSnap.data(),
          "Delivery/Pick-Up Date": newDate,
          updated: new Date().toISOString(),
        };

        const newRef = doc(db, targetCollection, newDocId);
        await setDoc(newRef, newPayload);

        // Delete old doc only when the ID or collection actually changed
        if (newDocId !== order.id || targetCollection !== sourceCollection) {
          await deleteDoc(oldRef);
        }

        if (isNewDatePast) {
          // Order moved to archive — remove from active list and close modal
          setFormsOrders(formsOrders.filter((o) => o.id !== order.id));
          onClose();
        } else {
          // Still active — update local state with new ID and new date
          setFormsOrders(
            formsOrders.map((o) =>
              o.id === order.id
                ? {
                    ...o,
                    id: newDocId,
                    deliveryDate: newDate,
                    originalDeliveryDate: newDate,
                  }
                : o
            )
          );
        }
        return;
      }

      // ── App orders ───────────────────────────────────────────────────────
      // App order IDs are not date-based; only collection moves on past dates.
      if (isNewDatePast && order._sourceCollection === "order") {
        const activeRef = doc(db, "order", order.id);
        const currentSnap = await getDoc(activeRef);
        if (currentSnap.exists()) {
          await setDoc(doc(db, "oldOrder", order.id), {
            ...currentSnap.data(),
            deliveryDate: newDate,
            updated: new Date().toISOString(),
          });
          await deleteDoc(activeRef);
        }
        onClose();
        return;
      }

      // App order — future date, update in place
      await setDoc(
        doc(db, order._sourceCollection || "order", order.id),
        { deliveryDate: newDate, updated: new Date().toISOString() },
        { merge: true }
      );
    } catch (error) {
      console.error("Error saving delivery date:", error);
    } finally {
      setDeliveryDateLoading(false);
    }
  };

  const getOrderDateType = (dateStr) => {
    if (!dateStr || !holidayCalendar || holidayCalendar.length === 0)
      return "weekday";
    const dow = new Date(dateStr).getDay();
    if (
      holidayCalendar.find(
        (e) => e.name === dateStr && e.id.startsWith("offday")
      )
    )
      return "offday";
    if (
      holidayCalendar.find(
        (e) => e.name === dateStr && e.id.startsWith("holiday")
      )
    )
      return "holiday";
    return dow === 0 || dow === 6 ? "weekend" : "weekday";
  };

  const getOrderTimeSlots = (dateType) => {
    if (!timeWindows || timeWindows.length === 0) return [];
    const prefix =
      dateType === "weekday"
        ? "weekdays"
        : dateType === "weekend" || dateType === "holiday"
        ? "weekend"
        : null;
    if (!prefix) return [];
    const startHour = (t) => parseInt(t, 10) || 0;
    return timeWindows
      .filter((e) => e.id && e.id.startsWith(prefix))
      .map((e) => e.name)
      .sort((a, b) => startHour(a) - startHour(b));
  };

  const isOrderDateOffDay = (dateStr) =>
    holidayCalendar &&
    !!holidayCalendar.find(
      (e) => e.name === dateStr && e.id.startsWith("offday")
    );

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPastDelivery = deliveryDateInput
    ? new Date(deliveryDateInput) < today
    : false;

  const saveDeliveryTime = async (val) => {
    const v = (val !== undefined ? val : deliveryTimeInput).trim();
    if (!user || !db || !order.id || deliveryTimeLoading || !v) return;
    if (v === (order.deliveryTime || "")) return;
    try {
      setDeliveryTimeLoading(true);
      const docRef = doc(db, order._sourceCollection || "FormOrders", order.id);
      const timePayload = order._isAppOrder
        ? { deliveryTime: v, updated: new Date().toISOString() }
        : { "Delivery Time": v, updated: new Date().toISOString() };
      await setDoc(docRef, timePayload, { merge: true });
      if (!order._isAppOrder) {
        setFormsOrders(
          formsOrders.map((o) =>
            o.id === order.id ? { ...o, deliveryTime: v } : o
          )
        );
      }
    } catch (error) {
      console.error("Error saving delivery time:", error);
    } finally {
      setDeliveryTimeLoading(false);
    }
  };

  const [offDayWarning, setOffDayWarning] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [editingTime, setEditingTime] = useState(false);

  const handleModalOverlayClick = (e) => {
    if (e.target.classList.contains("order-customer-modal-overlay")) {
      if (
        priceInput.trim() &&
        priceInput !==
          (order.cost
            ? order.cost.replace(" €", "").replace("€", "").trim()
            : "")
      ) {
        savePrice();
      }
      onClose();
    }
  };

  return (
    <>
      <div
        className="order-customer-modal-overlay order-zoom-modal-overlay"
        onClick={handleModalOverlayClick}
      >
        <div
          className="order-customer-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          {/* MODAL HEADER WITH CLOSE BUTTON */}
          <div className="order-customer-modal-header">
            <button
              className="order-customer-modal-close-btn"
              onClick={() => {
                if (
                  priceInput.trim() &&
                  priceInput !==
                    (order.cost
                      ? order.cost.replace(" €", "").replace("€", "").trim()
                      : "")
                ) {
                  savePrice();
                }
                onClose();
              }}
              title="Close"
            >
              ×
            </button>
          </div>

          {/* CURRENT ORDER DETAILS SECTION */}
          <div className="order-modal-section">
            <div
              className="order-modal-section-header"
              onClick={() => setExpandedCurrent(!expandedCurrent)}
            >
              <div className="order-section-title">
                <span className="order-expand-icon">
                  {expandedCurrent ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </div>
            </div>

            {expandedCurrent && (
              <div className="order-customer-modal-details">
                <div className="order-customer-detail-row">
                  <span className="order-customer-detail-label">Name</span>
                  <div className="order-customer-detail-value">
                    <span className="order-name-text">{name}</span>
                    <span
                      className="order-shop-rating-stars"
                      onClick={openShopFeedbackModal}
                      title={currentShopFeedback ? `Rating: ${currentShopFeedback.rating}/5 — click to edit` : "Click to rate customer"}
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <FaStar
                          key={star}
                          className={`order-shop-star ${
                            currentShopFeedback && currentShopFeedback.rating >= star
                              ? "filled"
                              : ""
                          }`}
                        />
                      ))}
                    </span>
                    {name && name !== "N/A" && (
                      <FaRegCopy
                        className="order-copy-action-icon"
                        onClick={() => navigator.clipboard.writeText(name)}
                      />
                    )}
                  </div>
                </div>

                <div className="order-customer-detail-row">
                  <span className="order-customer-detail-label">Email</span>
                  <div className="order-customer-detail-value">
                    <span className="order-email-text">{email}</span>
                    {email && email !== "N/A" && (
                      <FaRegCopy
                        className="order-copy-action-icon"
                        onClick={() => navigator.clipboard.writeText(email)}
                      />
                    )}
                  </div>
                </div>

                <div className="order-customer-detail-row">
                  <span className="order-customer-detail-label">Phone</span>
                  <div className="order-customer-detail-value">
                    <span className="order-phone-number-text">{phone}</span>
                    {phone && phone !== "N/A" && (
                      <FaPhoneAlt
                        className="order-phone-action-icon"
                        onClick={() => {
                          const cleanNumber = phone.replace(/[^0-9+]/g, "");
                          window.location.href = `tel:${cleanNumber}`;
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="order-customer-detail-row">
                  <span className="order-customer-detail-label">Address</span>
                  <div className="order-customer-detail-value">
                    <span className="order-address-text">{address}</span>
                    {address && address !== "N/A" && (
                      <FaMapMarkerAlt
                        className="order-map-action-icon"
                        onClick={() => {
                          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            address
                          )}`;
                          window.open(mapsUrl, "_blank");
                        }}
                      />
                    )}
                  </div>
                </div>

                {eventDate && (
                  <div className="order-customer-detail-row">
                    <span className="order-customer-detail-label">
                      Event Date
                    </span>
                    <span className="order-customer-detail-value">
                      {formatDate(eventDate)}
                    </span>
                  </div>
                )}

                <div className="order-customer-detail-row">
                  <span className="order-customer-detail-label">
                    {isPickup ? "Pick Up Date" : "Delivery Date"}
                  </span>
                  {(userRole === "Admin" || userRole === "Super") &&
                  (!isPastDelivery || editingDate) ? (
                    <div className="order-customer-detail-value">
                      {editingDate ? (
                        <input
                          type="date"
                          value={deliveryDateInput}
                          autoFocus
                          onChange={(e) => {
                            const v = e.target.value;
                            if (isOrderDateOffDay(v)) {
                              setDeliveryDateInput("");
                              setDeliveryTimeInput("");
                              setOffDayWarning(true);
                            } else {
                              setOffDayWarning(false);
                              const prevType =
                                getOrderDateType(deliveryDateInput);
                              const newType = getOrderDateType(v);
                              if (prevType !== newType)
                                setDeliveryTimeInput("");
                              setDeliveryDateInput(v);
                            }
                          }}
                          onBlur={() => {
                            saveDeliveryDate();
                            setEditingDate(false);
                          }}
                          disabled={deliveryDateLoading}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "14px",
                            outline: "none",
                            padding: "2px 4px",
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => setEditingDate(true)}
                          title="Click to change date"
                          style={{ cursor: "pointer", color: "inherit" }}
                        >
                          {formatDate(deliveryDateInput) || "N/A"}
                        </span>
                      )}
                      {deliveryDateLoading && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "12px",
                            color: "#999",
                          }}
                        >
                          Saving...
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="order-customer-detail-value">
                      {formatDate(deliveryDateInput || deliveryDate)}
                    </span>
                  )}
                </div>

                <div className="order-customer-detail-row">
                  <span className="order-customer-detail-label">
                    {isPickup ? "Pick Up Time" : "Delivery Time"}
                  </span>
                  {(userRole === "Admin" || userRole === "Super") &&
                  (!isPastDelivery || editingTime) ? (
                    <div className="order-customer-detail-value">
                      {editingTime ? (
                        (() => {
                          const dateType = getOrderDateType(deliveryDateInput);
                          const slots = getOrderTimeSlots(dateType);
                          return (
                            <select
                              autoFocus
                              value={deliveryTimeInput}
                              onChange={(e) => {
                                setDeliveryTimeInput(e.target.value);
                                saveDeliveryTime(e.target.value);
                                setEditingTime(false);
                              }}
                              onBlur={() => setEditingTime(false)}
                              disabled={
                                deliveryTimeLoading || !deliveryDateInput
                              }
                              style={{
                                border: "none",
                                background: "transparent",
                                fontSize: "14px",
                                outline: "none",
                                padding: "2px 4px",
                              }}
                            >
                              <option value="">Select Time</option>
                              {slots.map((s, i) => (
                                <option key={i} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          );
                        })()
                      ) : (
                        <span
                          onClick={() =>
                            deliveryDateInput && setEditingTime(true)
                          }
                          title={
                            deliveryDateInput
                              ? "Click to change time"
                              : "Select a date first"
                          }
                          style={{
                            cursor: deliveryDateInput ? "pointer" : "default",
                            color: "inherit",
                          }}
                        >
                          {deliveryTimeInput || order.deliveryTime || "N/A"}
                        </span>
                      )}
                      {deliveryTimeLoading && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "12px",
                            color: "#999",
                          }}
                        >
                          Saving...
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="order-customer-detail-value">
                      {order.deliveryTime || "N/A"}
                    </span>
                  )}
                </div>

                {offDayWarning &&
                  (userRole === "Admin" || userRole === "Super") && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#fa4848",
                        textAlign: "center",
                        padding: "2px 0 4px 0",
                      }}
                    >
                      Off Day, please select a different date.
                    </div>
                  )}

                <div className="order-customer-detail-row">
                  <span className="order-customer-detail-label">{`Price (${getCurrencySymbol()})`}</span>
                  <div className="order-customer-detail-value">
                    {(userRole === "Admin" || userRole === "Super") &&
                    (!isPastDelivery || isPriceMissing(order)) ? (
                      <>
                        <input
                          type="text"
                          value={priceInput}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^0-9.,]/g,
                              ""
                            );
                            setPriceInput(value);
                          }}
                          onBlur={savePrice}
                          placeholder="Enter price"
                          disabled={priceLoading}
                          className="order-price-input"
                          style={{
                            width: "80px",
                            padding: "2px 4px",
                            border: "none",
                            background: "transparent",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        {order._isAppOrder && order._delCost > 0 && (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              marginLeft: "4px",
                            }}
                          >
                            (incl. {order._delCost} {getCurrencySymbol()} delivery)
                          </span>
                        )}
                        {priceLoading && (
                          <span
                            style={{
                              marginLeft: "8px",
                              fontSize: "12px",
                              color: "#999",
                            }}
                          >
                            Saving...
                          </span>
                        )}
                      </>
                    ) : (
                      <span>{order.cost || "N/A"}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* OLDER ORDERS SECTION */}
          <div className="order-modal-section">
            <div
              className="order-modal-section-header"
              onClick={() => setExpandedOlderOrders(!expandedOlderOrders)}
            >
              <div className="order-section-title">
                <span className="order-expand-icon">
                  {expandedOlderOrders ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </div>
            </div>

            {expandedOlderOrders && (
              <div className="order-older-orders-container">
                {loadingOlderOrders ? (
                  <div className="order-loading-message">
                    Loading older orders...
                  </div>
                ) : olderOrders.length > 0 ? (
                  <table className="order-older-orders-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Rating</th>
                        <th>Date</th>
                        <th>Sponge Type</th>
                        <th>Flavour</th>
                        <th>Cover</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Matched By</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {olderOrders.map((oldOrder, index) => (
                        <tr
                          key={oldOrder.id || index}
                          className="order-older-order-row"
                        >
                          <td>
                            {oldOrder.image ? (
                              <img
                                src={oldOrder.image}
                                alt="Order"
                                className="order-order-image-small"
                                loading="lazy"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedImageUrl(oldOrder.image);
                                  setZoomModalOpen(true);
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="order-no-image">No Image</div>
                            )}
                          </td>
                          <td>
                            <span style={userProfileMap[oldOrder.userId]?.env === 'Test' ? { color: 'orange' } : {}}>
                              {oldOrder.name || "N/A"}
                            </span>
                          </td>
                          <td>
                            <span className="order-shop-rating-stars" style={{ cursor: 'default', pointerEvents: 'none' }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={star}
                                  className={`order-shop-star ${
                                    oldOrder.shopfeedback?.rating >= star ? 'filled' : ''
                                  }`}
                                />
                              ))}
                            </span>
                          </td>
                          <td>{formatDate(oldOrder.deliveryDate)}</td>
                          <td>{oldOrder.spongeType || "N/A"}</td>
                          <td>{oldOrder.flavour || "N/A"}</td>
                          <td>{oldOrder.cover || "N/A"}</td>
                          <td>{(() => { const q = cleanQuantity(oldOrder.quantity); if (!q) return 'N/A'; return oldOrder.matchedBy === 'userId' ? `${q} Kilogram` : q; })()}</td>
                          <td>{oldOrder.price || "N/A"}</td>
                          <td>{oldOrder.matchedBy || "N/A"}</td>
                          <td>{oldOrder.actualEmail || "N/A"}</td>
                          <td>{oldOrder.actualPhone || "N/A"}</td>
                          <td>{oldOrder.actualAddress || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="order-no-older-orders">
                    No older orders found.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {zoomModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ImageZoomModal
            isOpen={zoomModalOpen}
            onRequestClose={() => setZoomModalOpen(false)}
            imageUrl={selectedImageUrl}
          />
        </div>
      )}

      {showShopFeedbackModal && (
        <div
          className="order-shop-feedback-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowShopFeedbackModal(false); } }}
        >
          <div className="order-shop-feedback-popup">
            <div className="order-shop-feedback-header">
              Customer Rating
            </div>
            <div className="order-shop-feedback-body">
              <label className="order-shop-feedback-label">Overall Satisfaction</label>
              <div className="order-shop-feedback-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar
                    key={star}
                    className={`order-shop-star large ${
                      (shopFeedbackHover || shopFeedbackRating) >= star ? "filled" : ""
                    }`}
                    onClick={() => setShopFeedbackRating(star)}
                    onMouseEnter={() => setShopFeedbackHover(star)}
                    onMouseLeave={() => setShopFeedbackHover(0)}
                  />
                ))}
              </div>
              <label className="order-shop-feedback-label">
                Comment{" "}
                {shopFeedbackRating > 0 && shopFeedbackRating < 4 ? "(required)" : "(optional)"}
              </label>
              <textarea
                className="order-shop-feedback-textarea"
                value={shopFeedbackComment}
                onChange={(e) => setShopFeedbackComment(e.target.value)}
                placeholder="Enter feedback here..."
                rows={3}
              />
            </div>
            <div className="order-shop-feedback-btns">
              <button
                className="order-shop-feedback-btn"
                onClick={() => {
                  setShowShopFeedbackModal(false);
                  setShopFeedbackRating(0);
                  setShopFeedbackHover(0);
                  setShopFeedbackComment("");
                }}
              >
                Cancel
              </button>
              <button
                className="order-shop-feedback-btn primary"
                onClick={handleShopFeedbackSubmit}
                disabled={
                  shopFeedbackSaving ||
                  shopFeedbackRating === 0 ||
                  (shopFeedbackRating < 4 && shopFeedbackComment.trim() === "")
                }
              >
                {shopFeedbackSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Order;
